import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Box, Typography, CircularProgress, Alert, Paper, Grid, Button, IconButton, Menu, MenuItem, Snackbar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Fab, Chip, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ConnectingAirportsIcon from '@mui/icons-material/ConnectingAirports';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MapIcon from '@mui/icons-material/Map';
import TodayIcon from '@mui/icons-material/Today';
import GeminiIcon from './components/GeminiIcon';
import WandIcon from './components/WandIcon';
import './gemini-icon.css';
import './ai-icon.css';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { API_BASE_URL, API_PATH, BASE_PATH } from './config';
import TransportationFormModal from './TransportationFormModal';
import AddActivityModal from './AddActivityModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TripMap from './TripMap';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AIAssistant from './components/AIAssistant';
import AIIcon from './components/AIIcon';
import ActivityDetailsModal from './components/ActivityDetailsModal';
import ShareTripModal from './components/ShareTripModal';
import countriesCities from './data/countries_cities.json';
import dayjs from 'dayjs';
import ExpediaDateRangeInput from './components/ExpediaDateRangeInput';
import { getActivityDisplayTitle } from './utils/activityUtils';
import InteractiveDailyActivityBuilder from './components/InteractiveDailyActivityBuilder';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_API_KEY || '';

// ---------------------------------------------------------------------------
// Flight connection (layover) detection
// ---------------------------------------------------------------------------
// Returns whether an activity represents a flight, tolerating the several
// shapes flights can take (see memory: flight-activity-schema).
const isFlightActivity = (activity) => {
    if (!activity) return false;
    return activity.type === 'flight' ||
        activity.subtype === 'flight' ||
        (activity.type === 'transportation' && activity.subtype === 'flight');
};

// Extract origin/destination airport codes, cities and departure/arrival
// datetimes from a flight activity, normalizing across the possible schemas:
//  - Email-import / flat keys: origin_airport_code, destination_airport_code,
//    start_date_time, end_date_time (also origin_city / destination_city)
//  - Amadeus format: structured_data.details.departure/arrival.{iataCode,at}
//  - Legacy: structured_data.transportation_details.{from,to,...}
const getFlightEndpoints = (activity) => {
    if (!isFlightActivity(activity)) return null;
    let sd = activity.structured_data;
    if (typeof sd === 'string') { try { sd = JSON.parse(sd); } catch { sd = null; } }
    if (Array.isArray(sd)) sd = sd[0];
    sd = sd || {};

    // Pull each field from the most authoritative source available. A single
    // activity can carry several schemas at once (e.g. flat flight keys plus a
    // stale transportation_details block merged from the structured_data table),
    // so we prefer explicit flight keys / Amadeus details over legacy fields
    // rather than committing to one branch.
    const details = (sd.details && (sd.details.departure || sd.details.arrival)) ? sd.details : null;
    const td = sd.transportation_details || {};

    const originCode = sd.origin_airport_code || details?.departure?.iataCode || td.from || '';
    const destCode = sd.destination_airport_code || details?.arrival?.iataCode || td.to || '';
    const originCity = sd.origin_city || activity.location || '';
    const destCity = sd.destination_city || activity.destination || '';
    const departDT = sd.start_date_time || details?.departure?.at || td.departure_time || null;
    const arriveDT = sd.end_date_time || details?.arrival?.at || td.arrival_time || null;

    return {
        originCode: String(originCode).trim(),
        destCode: String(destCode).trim(),
        originCity: String(originCity).trim(),
        destCity: String(destCity).trim(),
        departDT: departDT || null,
        arriveDT: arriveDT || null,
    };
};

// Parse a "YYYY-MM-DD HH:mm" (or ISO) datetime into a Date, tolerating the
// space-separated form produced by the email importer.
// Parse a flight datetime as its wall-clock LOCAL time, ignoring any timezone
// designator/offset. A connection is always at one physical airport (flight 1's
// arrival airport == flight 2's departure airport), so both endpoints share a
// timezone and the naive wall-clock difference IS the true local layover — this
// is immune to the browser/runtime timezone and to mixed offset encodings.
// Backed by Date.UTC so getUTC*/getTime() reflect the raw wall-clock values.
const parseFlightDateTime = (value) => {
    if (!value) return null;
    const m = String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})/);
    if (!m) return null;
    const [, y, mo, d, h, mi] = m;
    return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi));
};

const isSameWallClockDay = (a, b) =>
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();

const formatLayoverDuration = (ms) => {
    const totalMinutes = Math.round(ms / 60000);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

// Given two consecutive activities, determine whether they form a connecting
// flight: the arrival airport of the first is the departure airport of the
// second AND the gap is under 12 hours (or both occur on the same calendar
// day). Returns connection info for display, or null when not a connection.
const getFlightConnection = (prev, next) => {
    const a = getFlightEndpoints(prev);
    const b = getFlightEndpoints(next);
    if (!a || !b) return null;

    // Arrival airport of first flight === departure airport of second flight.
    const arrCode = a.destCode.toUpperCase();
    const depCode = b.originCode.toUpperCase();
    const arrCity = a.destCity.toLowerCase();
    const depCity = b.originCity.toLowerCase();
    const sameByCode = arrCode && depCode && arrCode === depCode;
    const sameByCity = arrCity && depCity && arrCity === depCity;
    if (!sameByCode && !sameByCity) return null;

    const arriveDT = parseFlightDateTime(a.arriveDT);
    const departDT = parseFlightDateTime(b.departDT);

    let layoverText = '';
    let qualifies = false;
    if (arriveDT && departDT) {
        const gapMs = departDT.getTime() - arriveDT.getTime();
        if (gapMs < 0) return null; // second flight departs before first arrives
        const sameDay = isSameWallClockDay(arriveDT, departDT);
        const withinWindow = gapMs < 12 * 60 * 60 * 1000;
        qualifies = withinWindow || sameDay;
        layoverText = formatLayoverDuration(gapMs);
    } else {
        // No usable times: fall back to "same day" — these activities are
        // already grouped under the same day when this runs.
        qualifies = true;
    }
    if (!qualifies) return null;

    return {
        code: arrCode || depCode,
        city: a.destCity || b.originCity || '',
        layoverText,
    };
};

// Helper function to find country by city name
const findCountryByCity = (cityName) => {
    if (!cityName) return null;
    
    for (const countryData of countriesCities) {
        if (countryData.cities && countryData.cities.includes(cityName)) {
            return countryData.country;
        }
    }
    return null;
};

const cleanLocationPart = (text = '') => {
    return text
        .replace(/airport/gi, '')
        .replace(/terminal/gi, '')
        .replace(/international/gi, '')
        .replace(/domestic/gi, '')
        .trim();
};

const isGenericLocation = (value = '') => /(airport|terminal|international|domestic)/i.test(value);

const findFirstNonGeneric = (parts) => {
    for (const part of parts) {
        if (part && !isGenericLocation(part)) {
            return part;
        }
    }
    return '';
};

const extractCityCountryFromLocationString = (location) => {
    if (!location) return { city: '', country: '' };
    const parts = location.split(',').map(part => cleanLocationPart(part)).filter(Boolean);
    if (!parts.length) return { city: '', country: '' };

    const cityCandidate = findFirstNonGeneric(parts) || parts[0];
    const reversed = [...parts].reverse();
    const countryCandidate = findFirstNonGeneric(reversed) || reversed[0] || '';
    const resolvedCountry = findCountryByCity(cityCandidate) || countryCandidate;

    return {
        city: cityCandidate,
        country: resolvedCountry
    };
};

const parseCityCountryFromAddressComponents = (components = []) => {
    const hint = { city: '', country: '' };
    components.forEach((component) => {
        if (!component.types) return;
        if (!hint.city && (component.types.includes('locality') || component.types.includes('postal_town') || component.types.includes('administrative_area_level_1'))) {
            hint.city = component.long_name;
        }
        if (!hint.country && component.types.includes('country')) {
            hint.country = component.long_name;
        }
    });
    return hint;
};

const appendPartsFromCandidate = (candidate, parts) => {
    if (!candidate) return;
    if (Array.isArray(candidate)) {
        candidate.forEach(item => appendPartsFromCandidate(item, parts));
        return;
    }
    if (typeof candidate === 'string') {
        parts.push(candidate);
        return;
    }
    if (typeof candidate === 'object') {
        const fields = [
            candidate.city,
            candidate.cityName,
            candidate.displayName,
            candidate.name,
            candidate.airportName,
            candidate.location,
            candidate.locality,
            candidate.region,
            candidate.country,
            candidate.iataCode
        ];
        fields.forEach(value => {
            if (value) parts.push(value);
        });
    }
};

const buildTransportLocationString = (record) => {
    if (!record) return '';
    const parts = [];
    const details = record.details || {};
    appendPartsFromCandidate(details.arrival, parts);
    appendPartsFromCandidate(details.destination, parts);
    appendPartsFromCandidate(details.end, parts);
    appendPartsFromCandidate(details.to, parts);
    appendPartsFromCandidate(details.transportation_details?.arrival, parts);
    appendPartsFromCandidate(details.transportation_details?.to, parts);
    appendPartsFromCandidate(details.transportation_details?.destination, parts);
    appendPartsFromCandidate(details.transportation_details?.end, parts);
    appendPartsFromCandidate(record.arrival, parts);
    appendPartsFromCandidate(record.destination, parts);
    appendPartsFromCandidate(record.location, parts);
    appendPartsFromCandidate(record.to, parts);
    appendPartsFromCandidate(record.transportation_details?.arrival, parts);
    appendPartsFromCandidate(record.transportation_details?.to, parts);
    appendPartsFromCandidate(record.transportation_details?.destination, parts);
    appendPartsFromCandidate(record.transportation_details?.end, parts);
    appendPartsFromCandidate(record.end_location, parts);
    appendPartsFromCandidate(record.destination_city, parts);
    appendPartsFromCandidate(record.free_text, parts);
    return parts.filter(Boolean).join(', ');
};

const getTransportLocationHint = (transportationRecords = []) => {
    if (!Array.isArray(transportationRecords) || transportationRecords.length === 0) return null;
    for (let i = transportationRecords.length - 1; i >= 0; i -= 1) {
        const record = transportationRecords[i];
        const locationString = buildTransportLocationString(record);
        if (!locationString) continue;
        const extracted = extractCityCountryFromLocationString(locationString);
        return {
            city: extracted.city,
            country: extracted.country,
            locationName: locationString
        };
    }
    return null;
};

const buildHintFromString = (value, fallback, sourceName) => {
    if (!value) return null;
    const extracted = extractCityCountryFromLocationString(value);
    if (extracted.city || extracted.country) {
        return {
            city: extracted.city || fallback.city,
            country: extracted.country || fallback.country,
            locationName: value,
            source: sourceName
        };
    }
    return {
        city: fallback.city,
        country: fallback.country,
        locationName: value,
        source: sourceName
    };
};
const getActivityLocationHint = (activity, fallbackHint = { city: '', country: '' }) => {
    if (!activity) return null;
    const fallback = fallbackHint || { city: '', country: '' };
    
    // DEBUG: Log ENTIRE activity object to see what data we have
    console.error('[ACTIVITY LOCATION] FULL ACTIVITY OBJECT:', JSON.stringify(activity, null, 2));
    
    // MOST DIRECT APPROACH: Get location from activity and parse it
    const locationString = activity.location || activity.destination || activity.title || '';
    
    console.error('[ACTIVITY LOCATION] Extracting from activity:', {
        'activity.title': activity.title,
        'activity.location': activity.location,
        'activity.destination': activity.destination,
        'activity.city': activity.city,
        'activity.country': activity.country,
        'locationString': locationString,
        'allActivityKeys': Object.keys(activity).join(', ')
    });
    
    if (locationString) {
        // Try to extract city/country from the location string
        const extracted = extractCityCountryFromLocationString(locationString);
        console.error('[ACTIVITY LOCATION] Extracted from location string:', {
            locationString,
            extracted
        });
        
        if (extracted.city || extracted.country) {
            console.error('[ACTIVITY LOCATION] SUCCESS - Returning from activity location:', {
                city: extracted.city,
                country: extracted.country
            });
            return {
                city: extracted.city || fallback.city,
                country: extracted.country || fallback.country,
                locationName: locationString,
                source: 'activity location'
            };
        }
    }
    
    console.error('[ACTIVITY LOCATION] No extraction from location string, trying other fields...');
    
    // Try structured data if available
    const structuredData = Array.isArray(activity.structured_data)
        ? activity.structured_data[0]
        : activity.structured_data;
    const flightDetails = activity.details || structuredData?.details;
    const transportationDetails = structuredData?.transportation_details;

    const candidates = [
        activity.destination,
        activity.location,
        activity.arrival,
        activity.end_location,
        activity.to,
        activity.destination_city,
        transportationDetails?.to,
        transportationDetails?.destination,
        transportationDetails?.arrival,
        transportationDetails?.end
    ];

    console.warn('getActivityLocationHint: processing activity', {
        activityTitle: activity.title,
        candidates: candidates.filter(Boolean),
        structuredData
    });

    for (const candidate of candidates) {
        const hint = buildHintFromString(candidate, fallback, 'activity base');
        if (hint && hint.locationName) {
            // Ensure we always have city and country
            const extracted = extractCityCountryFromLocationString(hint.locationName);
            const result = {
                ...hint,
                city: hint.city || extracted.city || fallback.city,
                country: hint.country || extracted.country || fallback.country
            };
            console.warn('getActivityLocationHint: found location', {
                candidate,
                hint,
                extracted,
                result
            });
            return result;
        }
    }

    if (flightDetails) {
        const arrivalAirport = flightDetails.arrival;
        const arrivalParts = [];
        if (arrivalAirport?.airportName) arrivalParts.push(arrivalAirport.airportName);
        if (arrivalAirport?.city) arrivalParts.push(arrivalAirport.city);
        if (arrivalAirport?.country) arrivalParts.push(arrivalAirport.country);
        if (arrivalAirport?.iataCode) arrivalParts.push(`(${arrivalAirport.iataCode})`);
        const arrivalLocation = arrivalParts.filter(Boolean).join(' ').trim();
        const arrivalHint = buildHintFromString(arrivalLocation, fallback, 'flight arrival');
        if (arrivalHint && arrivalHint.locationName) {
            const lat = flightDetails?.geometry?.location?.lat;
            const lng = flightDetails?.geometry?.location?.lng;
            return {
                ...arrivalHint,
                locationLat: Number.isFinite(lat) ? lat : undefined,
                locationLng: Number.isFinite(lng) ? lng : undefined
            };
        }
    }

    if (structuredData) {
        const structuredHint = parseCityCountryFromAddressComponents(structuredData.address_components || []);
        const lat = structuredData.geometry?.location?.lat;
        const lng = structuredData.geometry?.location?.lng;
        const hintCity = structuredHint.city || fallback.city;
        const hintCountry = structuredHint.country || fallback.country;
        const resolvedLocationName = structuredData.formatted_address || activity.location || '';
        if (hintCity || hintCountry || resolvedLocationName) {
            return {
                city: hintCity,
                country: hintCountry,
                locationName: resolvedLocationName,
                locationLat: Number.isFinite(lat) ? lat : undefined,
                locationLng: Number.isFinite(lng) ? lng : undefined,
                source: 'structured data'
            };
        }
    }

    // Final fallback: try to extract city/country from activity title or location
    if (activity.title || activity.location) {
        const titleOrLocation = activity.title || activity.location || '';
        const extracted = extractCityCountryFromLocationString(titleOrLocation);
        if (extracted.city || extracted.country) {
            return {
                city: extracted.city || fallback.city,
                country: extracted.country || fallback.country,
                locationName: titleOrLocation,
                source: 'activity title/location'
            };
        }
    }

    return null;

    if (flightDetails) {
        const arrivalAirport = flightDetails.arrival;
        const arrivalParts = [];
        if (arrivalAirport?.airportName) arrivalParts.push(arrivalAirport.airportName);
        if (arrivalAirport?.city) arrivalParts.push(arrivalAirport.city);
        if (arrivalAirport?.country) arrivalParts.push(arrivalAirport.country);
        if (arrivalAirport?.iataCode) arrivalParts.push(`(${arrivalAirport.iataCode})`);
        const arrivalLocation = arrivalParts.filter(Boolean).join(' ').trim();
        const arrivalHint = buildHintFromString(arrivalLocation, fallback, 'flight arrival');
        if (arrivalHint && arrivalHint.locationName) {
            const lat = flightDetails?.geometry?.location?.lat;
            const lng = flightDetails?.geometry?.location?.lng;
            return {
                ...arrivalHint,
                locationLat: Number.isFinite(lat) ? lat : undefined,
                locationLng: Number.isFinite(lng) ? lng : undefined
            };
        }
    }

    if (structuredData) {
        const structuredHint = parseCityCountryFromAddressComponents(structuredData.address_components || []);
        const lat = structuredData.geometry?.location?.lat;
        const lng = structuredData.geometry?.location?.lng;
        const hintCity = structuredHint.city || fallback.city;
        const hintCountry = structuredHint.country || fallback.country;
        const resolvedLocationName = structuredData.formatted_address || activity.location || '';
        if (hintCity || hintCountry || resolvedLocationName) {
            return {
                city: hintCity,
                country: hintCountry,
                locationName: resolvedLocationName,
                locationLat: Number.isFinite(lat) ? lat : undefined,
                locationLng: Number.isFinite(lng) ? lng : undefined,
                source: 'structured data'
            };
        }
    }

    // Final fallback: try to extract city/country from activity title or location
    if (activity.title || activity.location) {
        const titleOrLocation = activity.title || activity.location || '';
        const extracted = extractCityCountryFromLocationString(titleOrLocation);
        if (extracted.city || extracted.country) {
            return {
                city: extracted.city || fallback.city,
                country: extracted.country || fallback.country,
                locationName: titleOrLocation,
                source: 'activity title/location'
            };
        }
    }

    return null;
};

const getDayLocationHint = (day, activitiesByDay, fallbackLocation = '', transportationRecords = [], tripDays = []) => {
    if (!day) return { city: '', country: '' };
    
    // First priority: use day.city if it's already set (e.g. from trip route)
    if (day.city) {
        return {
            city: day.city,
            country: day.country || '',
            locationName: day.city,
            source: 'day-city'
        };
    }
    
    // Try activities first - look for any activity with location data
    const activitiesForDay = activitiesByDay?.[day.date] || [];
    
    if (activitiesForDay.length > 0) {
        // Try to find the first activity with a valid location
        for (const activity of activitiesForDay) {
            const locationString = activity?.location || activity?.destination || '';
            
            if (locationString && typeof locationString === 'string' && locationString.length > 0) {
                // Try to extract city from location string
                // Simple approach: look for city name (usually first part before comma, or after common keywords)
                const cleanLoc = locationString.trim();
                
                // Try extracting with our helper function
                const extracted = extractCityCountryFromLocationString(cleanLoc);
                if (extracted.city) {
                    return {
                        city: extracted.city,
                        country: extracted.country,
                        locationName: cleanLoc,
                        source: 'activity'
                    };
                }
                
                // Fallback: take first meaningful part before comma
                const parts = cleanLoc.split(',').map(p => p.trim()).filter(Boolean);
                if (parts.length > 0) {
                    const cityCandidate = parts[0];
                    // Avoid generic terms
                    if (cityCandidate && !/^(airport|terminal|station)$/i.test(cityCandidate)) {
                        return {
                            city: cityCandidate,
                            country: parts[parts.length - 1] || '',
                            locationName: cleanLoc,
                            source: 'activity-simple'
                        };
                    }
                }
            }
        }
    }
    
    // Try fallbackLocation (from trip)
    if (fallbackLocation && typeof fallbackLocation === 'string' && fallbackLocation.length > 0) {
        const extracted = extractCityCountryFromLocationString(fallbackLocation);
        if (extracted.city) {
            return {
                city: extracted.city,
                country: extracted.country,
                locationName: fallbackLocation,
                source: 'trip location'
            };
        }
    }
    
    return { city: '', country: '' };
};

const getViatorQuickPriceSymbol = (priceValue) => {
    const priceNumber = Number(priceValue);
    if (!priceValue || Number.isNaN(priceNumber) || priceNumber <= 0) {
        return '--';
    }
    if (priceNumber < 25) {
        return '$';
    }
    if (priceNumber < 75) {
        return '$$';
    }
    if (priceNumber < 200) {
        return '$$$';
    }
    return '--';
};

// Helper function to generate Expedia affiliate link for flights
// isRoundtrip: true for first/last day flights, false for inner travel segments
const generateExpediaFlightLink = (activity, tripStartDate, tripEndDate, activityDate, isRoundtrip = true) => {
    // Extract flight details from activity - check multiple possible data structures
    const structuredData = activity.structured_data || {};
    const flightDetails = structuredData.details || structuredData.flight_details || structuredData.transportation_details || {};
    
    // Get departure and arrival codes from multiple possible sources
    let departureCode = flightDetails.departure?.iataCode 
        || flightDetails.departure_airport 
        || structuredData.departure_airport 
        || activity.departure_airport
        || '';
    let arrivalCode = flightDetails.arrival?.iataCode 
        || flightDetails.arrival_airport 
        || structuredData.arrival_airport 
        || activity.arrival_airport
        || '';
    
    // If no IATA codes found, try to extract from title
    if (!departureCode || !arrivalCode) {
        // Title format 1: "TLV (Israel) -> PRG (Czechia)" - extract IATA codes
        const iataMatch = activity.title?.match(/^([A-Z]{3})\s*\([^)]+\)\s*->\s*([A-Z]{3})\s*\([^)]+\)$/i);
        if (iataMatch) {
            departureCode = departureCode || iataMatch[1].toUpperCase();
            arrivalCode = arrivalCode || iataMatch[2].toUpperCase();
        }
        
        // Title format 2: "Travel from City1 to City2" - extract city names
        if (!departureCode || !arrivalCode) {
            const travelMatch = activity.title?.match(/Travel from (.+?) to (.+)/i);
            if (travelMatch) {
                departureCode = departureCode || travelMatch[1].trim();
                arrivalCode = arrivalCode || travelMatch[2].trim();
            }
        }
    }
    
    console.log('generateExpediaFlightLink - activity:', activity.title);
    console.log('generateExpediaFlightLink - departureCode:', departureCode, 'arrivalCode:', arrivalCode);
    console.log('generateExpediaFlightLink - isRoundtrip:', isRoundtrip, 'activityDate:', activityDate);
    console.log('generateExpediaFlightLink - tripStartDate:', tripStartDate, 'tripEndDate:', tripEndDate);
    
    if (!departureCode || !arrivalCode) {
        console.log('generateExpediaFlightLink - Missing airport/city info, returning null');
        return null;
    }
    
    // Format dates for Expedia (M/D/YYYY format)
    const formatDateForExpedia = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        const month = date.getMonth() + 1; // JavaScript months are 0-indexed
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };
    
    // Build Expedia search URL
    const expediaParams = new URLSearchParams();
    
    if (isRoundtrip) {
        // Roundtrip for first/last day flights
        const departureDate = formatDateForExpedia(tripStartDate);
        const returnDate = formatDateForExpedia(tripEndDate);
        
        if (!departureDate || !returnDate) {
            console.log('generateExpediaFlightLink - Missing dates for roundtrip, returning null');
            return null;
        }
        
        expediaParams.set('trip', 'Roundtrip');
        expediaParams.set('leg1', `from:${departureCode},to:${arrivalCode},departure:${departureDate}TANYT`);
        expediaParams.set('leg2', `from:${arrivalCode},to:${departureCode},departure:${returnDate}TANYT`);
    } else {
        // One-way for inner travel segments
        const flightDate = formatDateForExpedia(activityDate);
        
        if (!flightDate) {
            console.log('generateExpediaFlightLink - Missing date for one-way flight, returning null');
            return null;
        }
        
        expediaParams.set('trip', 'Oneway');
        expediaParams.set('leg1', `from:${departureCode},to:${arrivalCode},departure:${flightDate}TANYT`);
    }
    
    expediaParams.set('passengers', 'children:0,adults:1,seniors:0,infantinlap:Y');
    expediaParams.set('options', 'sortby:price');
    expediaParams.set('mode', 'search');
    expediaParams.set('paandi', 'true');
    
    const expediaUrl = `https://www.expedia.com/Flights-Search?${expediaParams.toString()}`;
    
    // Wrap with CJ affiliate link
    const affiliateBaseUrl = 'https://www.anrdoezrs.net/click-101602496-14580833';
    const affiliateUrl = `${affiliateBaseUrl}?url=${encodeURIComponent(expediaUrl)}`;
    
    console.log('generateExpediaFlightLink - Generated URL:', affiliateUrl);
    return affiliateUrl;
};

// ── Pricing / cost helpers ─────────────────────────────────────────────────
// Cost details are captured from AI itinerary import and inbound email parsing
// into structured_data (keys: price, currency, price_per_night). We present them
// on activity / lodging / flight cards when available.
const CURRENCY_SYMBOLS = {
    USD: '$', EUR: '€', GBP: '£', ILS: '₪', JPY: '¥', CNY: '¥', KRW: '₩',
    INR: '₹', THB: '฿', CHF: 'CHF', CAD: 'C$', AUD: 'A$', NZD: 'NZ$',
};
const parseMoneyNumber = (val) => {
    if (val === null || val === undefined) return null;
    const n = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
};
const formatMoney = (amount, currency) => {
    if (amount === null || amount === undefined) return '';
    const num = Number(amount);
    const body = Number.isInteger(num)
        ? num.toLocaleString()
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const cur = (currency || '').trim();
    if (!cur) return body;                                         // unknown currency → no symbol (don't imply USD)
    const upper = cur.toUpperCase();
    if (CURRENCY_SYMBOLS[upper]) return `${CURRENCY_SYMBOLS[upper]}${body}`;
    if (/^[^A-Za-z0-9]+$/.test(cur)) return `${cur}${body}`;      // already a symbol
    return `${body} ${cur}`;                                       // unknown code → suffix
};
// Build a human cost summary from an activity's structured_data.
// Lodging: prefer a per-night rate; otherwise derive nightly from total / nights.
const getActivityCostInfo = (activity) => {
    if (!activity) return null;
    let sd = activity.structured_data;
    if (Array.isArray(sd)) sd = sd[0];
    if (typeof sd === 'string') { try { sd = JSON.parse(sd); } catch (e) { sd = {}; } }
    sd = sd || {};

    const currency = sd.currency || sd.price_currency || '';
    const total = parseMoneyNumber(sd.price ?? sd.cost ?? sd.total_price ?? sd.amount);
    const perNight = parseMoneyNumber(sd.price_per_night ?? sd.cost_per_night ?? sd.nightly_price);
    const nights = parseInt(sd.nights, 10) || null;
    const isLodging = activity.type === 'lodging' || activity.type === 'Lodging';

    if (isLodging) {
        if (perNight) {
            const totalCalc = total || (nights ? perNight * nights : null);
            let s = `${formatMoney(perNight, currency)}/night`;
            if (totalCalc) {
                s += ` · ${formatMoney(totalCalc, currency)} total`;
                if (nights) s += ` (${nights} night${nights > 1 ? 's' : ''})`;
            }
            return s;
        }
        if (total) {
            if (nights && nights > 1) {
                return `${formatMoney(total / nights, currency)}/night · ${formatMoney(total, currency)} total (${nights} nights)`;
            }
            return `${formatMoney(total, currency)} total`;
        }
        return null;
    }
    // Flights & activities: show the price the traveller paid.
    if (total) return formatMoney(total, currency);
    if (perNight) return formatMoney(perNight, currency);
    return null;
};

// Helper function to parse distance and duration from notes and determine if flight or car
const parseDistanceAndDuration = (notes) => {
    if (!notes) return null;
    const lines = notes.split(/\r?\n/);
    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.toLowerCase().startsWith('distance:')) continue;
        const match = line.match(/Distance:\s*([^\(]+)(?:\s*\(([^)]+)\))?/i);
        if (match) {
            return {
                distance: match[1].trim(),
                duration: match[2] ? match[2].trim() : null
            };
        }
    }
    return null;
};

// Helper function to determine if should show flight (>250km or >2.5 hours) or car (<250km and <2.5 hours)
const shouldShowFlightIcon = (distanceInfo) => {
    if (!distanceInfo) return false;
    
    // Check distance: if >= 250 km, show flight
    const distanceMatch = distanceInfo.distance?.match(/(\d+(?:\.\d+)?)\s*(km|mi)/i);
    if (distanceMatch) {
        const distance = parseFloat(distanceMatch[1]);
        const unit = distanceMatch[2].toLowerCase();
        const distanceInKm = unit === 'mi' ? distance * 1.60934 : distance;
        if (distanceInKm >= 250) return true;
    }
    
    // Check duration: if >= 2.5 hours (2:30), show flight
    const durationMatch = distanceInfo.duration?.match(/(\d+):(\d+)/);
    if (durationMatch) {
        const hours = parseInt(durationMatch[1], 10);
        const minutes = parseInt(durationMatch[2], 10);
        const totalMinutes = hours * 60 + minutes;
        if (totalMinutes >= 150) return true; // 2.5 hours = 150 minutes
    }
    
    return false;
};

// Helper function to generate Expedia car rental affiliate link
const generateExpediaCarRentalLink = (activity, dayDate) => {
    const structuredData = activity.structured_data || {};
    const transportDetails = structuredData.details || structuredData.transportation_details || {};
    
    let departureLocation = transportDetails.departure_location || structuredData.departure_location || '';
    let arrivalLocation = transportDetails.arrival_location || structuredData.arrival_location || '';
    
    // Extract from title if needed: "Travel from City1 to City2"
    if (!departureLocation || !arrivalLocation) {
        const travelMatch = activity.title?.match(/Travel from (.+?) to (.+)/i);
        if (travelMatch) {
            departureLocation = departureLocation || travelMatch[1].trim();
            arrivalLocation = arrivalLocation || travelMatch[2].trim();
        }
    }
    
    if (!departureLocation || !arrivalLocation || !dayDate) {
        console.log('generateExpediaCarRentalLink - Missing location or date info');
        return null;
    }
    
    // Format date for Expedia
    const date = new Date(dayDate);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const dateStr = `${month}/${day}/${year}`;
    
    // For car rental, use return date as next day
    const returnDate = new Date(date);
    returnDate.setDate(returnDate.getDate() + 1);
    const returnMonth = (returnDate.getMonth() + 1).toString().padStart(2, '0');
    const returnDay = returnDate.getDate().toString().padStart(2, '0');
    const returnYear = returnDate.getFullYear();
    const returnDateStr = `${returnMonth}/${returnDay}/${returnYear}`;
    
    // Build car search parameters
    const carSearchParams = new URLSearchParams({
        locn: departureLocation,
        loc2: arrivalLocation,
        d1: dateStr,
        d2: returnDateStr,
        date1: dateStr,
        date2: returnDateStr,
        time1: '1030AM',
        time2: '1030AM'
    });
    
    const expediaUrl = `https://www.expedia.com/carsearch?${carSearchParams.toString()}`;
    
    // Wrap with CJ affiliate link for car rentals
    const affiliateBaseUrl = 'https://www.kqzyfj.com/click-101602496-14580836';
    const affiliateUrl = `${affiliateBaseUrl}?url=${encodeURIComponent(expediaUrl)}`;
    
    console.log('generateExpediaCarRentalLink - Generated URL:', affiliateUrl);
    return affiliateUrl;
};

const generateExpediaHotelSearchLink = (locationData, checkInDate, checkOutDate, adults = 2, rooms = 1) => {
    if (!locationData || !checkInDate || !checkOutDate) {
        console.log('generateExpediaHotelSearchLink - Missing required parameters');
        return null;
    }
    
    // Handle both string and object location formats
    let destination = '';
    if (typeof locationData === 'string') {
        destination = locationData;
    } else if (typeof locationData === 'object' && locationData.locationName) {
        // Use the full location name if available (includes city, country info)
        destination = locationData.locationName;
    } else if (typeof locationData === 'object' && locationData.city) {
        // Build from city and country
        if (locationData.country) {
            destination = `${locationData.city}, ${locationData.country}`;
        } else {
            destination = locationData.city;
        }
    }
    
    if (!destination) {
        console.log('generateExpediaHotelSearchLink - Could not extract destination from location data');
        return null;
    }
    
    // Format dates for Expedia (YYYY-MM-DD)
    const checkInDateObj = new Date(checkInDate);
    const checkOutDateObj = new Date(checkOutDate);
    
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    const checkInStr = formatDate(checkInDateObj);
    const checkOutStr = formatDate(checkOutDateObj);
    
    // Build hotel search URL
    // destination format: "Los Angeles (and vicinity), California, United States of America"
    const hotelSearchParams = new URLSearchParams({
        destination: destination,
        flexibility: '0_DAY',
        d1: checkInStr,
        startDate: checkInStr,
        d2: checkOutStr,
        endDate: checkOutStr,
        adults: adults.toString(),
        rooms: rooms.toString(),
        sort: 'RECOMMENDED',
        useRewards: 'true'
    });
    
    const expediaUrl = `https://www.expedia.com/Hotel-Search?${hotelSearchParams.toString()}`;
    
    // Wrap with CJ affiliate link for hotels
    const affiliateBaseUrl = 'https://www.jdoqocy.com/click-101602496-14580829';
    const affiliateUrl = `${affiliateBaseUrl}?url=${encodeURIComponent(expediaUrl)}`;
    
    console.log('generateExpediaHotelSearchLink - Generated URL:', affiliateUrl);
    console.log('generateExpediaHotelSearchLink - Destination:', destination);
    return affiliateUrl;
};


// ActivityItem component for individual activities (shows start/end time, warning icon for overlap)
const ActivityItem = ({ activity, onDelete, nextActivity, previousActivity, onEdit, onFlightUpdate, onFlightUpdateDisabled, isTripFlexible, arrivalFlightActivity, onAskAI, onShowDetails, dayLabel, dayDate, onOpenTransportation, hasTransportationInfo = false, isDragging = false, dragHandleProps = {}, onPin, trip }) => {
    const { t } = useTranslation();
    const [showDetails, setShowDetails] = useState(false);
    const [amadeusFlightCost, setAmadeusFlightCost] = useState(null);
    const [loadingAmadeusCost, setLoadingAmadeusCost] = useState(false);
    const [flightFetchAttempted, setFlightFetchAttempted] = useState(false);
    const [showOverlapError, setShowOverlapError] = useState(false);
    const hasInitiatedFlightFetch = useRef(false); // Track if we've already initiated fetch for this activity
    const [showBeforeArrivalError, setShowBeforeArrivalError] = useState(false);
    const [showPreviousOverlapError, setShowPreviousOverlapError] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [thumbnailUrl, setThumbnailUrl] = useState(activity.preview_image_url || null);
    const thumbnailFetchRef = useRef(false);
    const [sourceEmailMsg, setSourceEmailMsg] = useState(null);
    const [loadingSourceEmail, setLoadingSourceEmail] = useState(false);
    const menuOpen = Boolean(anchorEl);

    const handleViewSourceEmail = async (e) => {
        e.stopPropagation();
        if (!activity.source_message_id) return;
        setLoadingSourceEmail(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/get_message.php?id=${activity.source_message_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setSourceEmailMsg(await res.json());
            }
        } catch (err) {
            console.error('Failed to load source email', err);
        } finally {
            setLoadingSourceEmail(false);
        }
    };
    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    
    const handleInfo = () => {
        onShowDetails(activity);
        handleMenuClose();
    };
    
    const handleActivityClick = () => {
        // Open details modal when clicking on activity
        onShowDetails(activity);
    };
    
    const handleEdit = () => {
        // Check if this is a lodging activity
        if (activity.type === 'lodging') {
            // Open hotel dialog instead of edit modal for lodging
            setSelectedHotelDay({ 
                date: dayDate || activity.date, 
                dayNumber: activity.day 
            });
            setSelectedHotelStayId(activity.activity_id);
            setHotelDialogOpen(true);
        } else {
            // For regular activities, open the edit modal
            onEdit(activity);
        }
        handleMenuClose();
    };
    
    const handleDelete = () => {
        onDelete(activity.activity_id);
        handleMenuClose();
    };
    
    const handlePin = () => {
        if (onPin) {
            onPin(activity.activity_id, !activity.is_pinned);
        }
        handleMenuClose();
    };
    
    const handleFlightUpdate = () => {
        if (isTripFlexible && onFlightUpdateDisabled) {
            onFlightUpdateDisabled();
        } else if (onFlightUpdate) {
            onFlightUpdate(activity);
        }
    };
    
    // Fetch Amadeus flight cost for flights - only once per activity
    useEffect(() => {
        // Only fetch if it's a flight and we haven't already initiated a fetch for this activity
        if ((activity.type === 'flight' || activity.subtype === 'flight') && !hasInitiatedFlightFetch.current) {
            console.log('ActivityItem useEffect - Fetching flight cost for:', activity.activity_id);
            hasInitiatedFlightFetch.current = true;
            fetchAmadeusFlightCost();
        }
    }, [activity.activity_id, activity.type, activity.subtype]); // Only depend on activity properties

    // Fetch thumbnail for regular activities (not flights, lodging, or travel segments)
    useEffect(() => {
        if (
            !thumbnailUrl &&
            !thumbnailFetchRef.current &&
            activity.type !== 'flight' &&
            activity.type !== 'transportation' &&
            activity.type !== 'lodging' &&
            activity.type !== 'Lodging' &&
            Number(activity.type) !== 1 &&
            !activity.title?.startsWith('Travel from')
        ) {
            thumbnailFetchRef.current = true;
            const token = localStorage.getItem('token');
            const locationParts = (activity.location || '').split(',').map(p => p.trim());
            const activityCity = locationParts[0] || '';
            const activityCountry = locationParts.length > 1 ? locationParts[locationParts.length - 1] : '';
            fetch(`${API_PATH}/trips/get_activity_preview_image.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    activity_name: activity.title,
                    city: activityCity,
                    country: activityCountry,
                    activity_id: activity.activity_id
                })
            })
            .then(r => r.json())
            .then(data => { if (data.success && data.image_url) setThumbnailUrl(data.image_url); })
            .catch(() => {});
        }
    }, [activity.activity_id]); // eslint-disable-line
    
    const fetchAmadeusFlightCost = async () => {
        try {
            setLoadingAmadeusCost(true);
            setFlightFetchAttempted(true);
            
            let origin, destination;
            
            // For type='flight' activities, extract IATA codes from title like "TLV (Israel) -> PRG (Czechia)"
            if (activity.type === 'flight') {
                const iataMatch = activity.title?.match(/^([A-Z]{3})\s*\([^)]+\)\s*->\s*([A-Z]{3})\s*\([^)]+\)$/i);
                if (!iataMatch) {
                    console.log('fetchAmadeusFlightCost - Could not extract IATA codes from title:', activity.title);
                    setLoadingAmadeusCost(false);
                    return;
                }
                origin = iataMatch[1];
                destination = iataMatch[2];
            } else if (activity.subtype === 'flight') {
                // For transportation/travel segment flights, extract city names from title like "Travel from City1 to City2"
                const travelMatch = activity.title?.match(/Travel from (.+?) to (.+)/i);
                if (travelMatch) {
                    origin = travelMatch[1].trim();
                    destination = travelMatch[2].trim();
                    console.log('fetchAmadeusFlightCost - Extracted cities from travel segment:', origin, 'to', destination);
                } else {
                    console.log('fetchAmadeusFlightCost - Could not extract cities from title:', activity.title);
                    setLoadingAmadeusCost(false);
                    return;
                }
            } else {
                console.log('fetchAmadeusFlightCost - Not a flight activity');
                setLoadingAmadeusCost(false);
                return;
            }
            
            const flightDate = dayDate || trip?.start_date;
            
            if (!flightDate) {
                console.log('fetchAmadeusFlightCost - Missing flight date');
                setLoadingAmadeusCost(false);
                return;
            }
            
            console.log('fetchAmadeusFlightCost - Fetching for', origin, 'to', destination, 'on', flightDate);
            
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                origin: origin,
                destination: destination,
                date: flightDate
            });
            const response = await fetch(`${API_PATH}/search_flights_for_route.php?${params.toString()}`, {
                method: 'GET',
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            
            console.log('fetchAmadeusFlightCost - Response status:', response.status);
            
            if (response.ok) {
                const data = await response.json();
                console.log('fetchAmadeusFlightCost - Response data:', data);
                if (data.data && data.data.length > 0) {
                    // Calculate price range from all available flights
                    const prices = data.data
                        .map(flight => parseFloat(flight.price || flight.price?.total))
                        .filter(price => !isNaN(price));
                    
                    if (prices.length > 0) {
                        const minPrice = Math.min(...prices);
                        const maxPrice = Math.max(...prices);
                        
                        if (minPrice === maxPrice) {
                            console.log('fetchAmadeusFlightCost - Setting single price:', minPrice);
                            setAmadeusFlightCost(`$${Math.round(minPrice)}`);
                        } else {
                            console.log('fetchAmadeusFlightCost - Setting price range:', minPrice, '-', maxPrice);
                            setAmadeusFlightCost(`$${Math.round(minPrice)}-${Math.round(maxPrice)}`);
                        }
                    } else {
                        console.log('fetchAmadeusFlightCost - No valid prices found');
                        setAmadeusFlightCost(null);
                    }
                } else {
                    console.log('fetchAmadeusFlightCost - No flights found in response');
                    setAmadeusFlightCost(null);
                }
            } else {
                const errorText = await response.text();
                console.log('fetchAmadeusFlightCost - Error response:', errorText);
                // Show message that flight data is unavailable (test API has limited data)
                setAmadeusFlightCost(null);
            }
        } catch (error) {
            console.error('Error fetching Amadeus flight cost:', error);
            setAmadeusFlightCost(null);
        } finally {
            setLoadingAmadeusCost(false);
        }
    };
    
    // Debug: log activity props
    console.log('ActivityItem:', activity);
    console.log('Activity type:', activity.type);
    console.log('Is flight?', activity.type === 'flight');
    
    // Calculate end time
    let endTime = '';
    let endsNextDay = false;
    const duration = activity.duration_min ?? activity.duration;
    
    if (activity.start_time && duration) {
        const [h, m] = activity.start_time.split(':').map(Number);
        const startDate = new Date(0, 0, 0, h, m);
        startDate.setMinutes(startDate.getMinutes() + Number(duration));
        const eh = startDate.getHours().toString().padStart(2, '0');
        const em = startDate.getMinutes().toString().padStart(2, '0');
        endTime = `${eh}:${em}`;
        
        // Check if activity ends the next day (end time < start time)
        if (endTime < activity.start_time.slice(0,5)) {
            endsNextDay = true;
        }
    }
    if (!endTime) {
        endTime = '--:--';
    }

    // Check if activity is before arrival flight
    const isLodgingActivity = activity.type === 'lodging' || activity.type === 'Lodging' || Number(activity.type) === 1;
    const shouldCheckConflicts = !isLodgingActivity;

    // Check for overlap with next activity
    let overlap = false;
    if (shouldCheckConflicts && endTime && endTime !== '--:--' && nextActivity && nextActivity.start_time) {
        if (endsNextDay) {
            overlap = true;
        } else {
            overlap = endTime > nextActivity.start_time.slice(0,5);
        }
    }

    // Check if previous activity ends after this one starts
    let previousOverlap = false;
    if (shouldCheckConflicts && previousActivity && previousActivity.start_time) {
        const prevDuration = previousActivity.duration_min ?? previousActivity.duration;
        if (prevDuration) {
            const [ph, pm] = previousActivity.start_time.split(':').map(Number);
            const prevStartDate = new Date(0, 0, 0, ph, pm);
            prevStartDate.setMinutes(prevStartDate.getMinutes() + Number(prevDuration));
            const prevEndHour = prevStartDate.getHours().toString().padStart(2, '0');
            const prevEndMin = prevStartDate.getMinutes().toString().padStart(2, '0');
            const prevEndTime = `${prevEndHour}:${prevEndMin}`;
            const prevEndsNextDay = prevEndTime < previousActivity.start_time.slice(0,5);
            
            const currentStartTime = activity.start_time ? activity.start_time.slice(0,5) : '00:00';
            
            if (prevEndsNextDay) {
                previousOverlap = true;
            } else {
                previousOverlap = prevEndTime > currentStartTime;
            }
        }
    }

    let isBeforeArrival = false;
    if (shouldCheckConflicts && arrivalFlightActivity && activity.activity_id !== arrivalFlightActivity.activity_id && activity.type !== 'flight') {
        if (activity.day < arrivalFlightActivity.day) {
            isBeforeArrival = true;
        } else if (activity.day === arrivalFlightActivity.day) {
            const activityTime = activity.start_time ? activity.start_time.slice(0,5) : '00:00';
            const arrivalTime = arrivalFlightActivity.start_time ? arrivalFlightActivity.start_time.slice(0,5) : '00:00';
            if (activityTime < arrivalTime) {
                isBeforeArrival = true;
            }
        }
    }

    // Prepare location label and border color
    // Treat "0" (Gemini placeholder for missing field) same as null
    const rawLocation = (activity.location && String(activity.location) !== '0') ? activity.location : null;
    const fullLocation = rawLocation || t('suggested_location');
    const parts = fullLocation.split(',').map(p => p.trim());
    const city = parts[0] || '';
    const region = parts.length > 1 ? parts[parts.length - 1] : '';
    
    // Look up country by city name
    const country = findCountryByCity(city);
    
    // Create location label with country
    let locationLabel;
    if (country) {
        locationLabel = region && region !== country ? `${city}, ${country}` : `${city}, ${country}`;
    } else {
        locationLabel = region ? `${city}, ${region}` : city || t('suggested_location');
    }

    const isTravelSegment = activity.type === 'transportation' && activity.title?.startsWith('Travel from');
    const distanceInfo = parseDistanceAndDuration(activity.notes);
    
    // For travel segments, determine if should show flight or car icon based on distance/duration
    // If distance is short (<250km and <2.5 hours), show car icon even if subtype is 'flight'
    let isLongDistance = shouldShowFlightIcon(distanceInfo);
    
    // If no distance info but this is international travel, assume flight
    if (!isLongDistance && isTravelSegment && activity.subtype === 'flight') {
        // Extract origin and destination from title like "Travel from City A to City B"
        const travelMatch = activity.title?.match(/Travel from (.+?) to (.+)/i);
        if (travelMatch) {
            const originCity = travelMatch[1].trim();
            const destinationCity = travelMatch[2].trim();
            const originCountry = findCountryByCity(originCity);
            const destCountry = findCountryByCity(destinationCity);
            
            // If countries are different, it's international travel - show flight
            if (originCountry && destCountry && originCountry !== destCountry) {
                isLongDistance = true;
            }
        }
    }
    
    let effectiveSubtype = activity.subtype;
    if (isTravelSegment && !isLongDistance && activity.subtype === 'flight') {
        // Short distance flight should be shown as car rental
        effectiveSubtype = 'car';
    }
    
    const travelLabel = effectiveSubtype === 'flight'
        ? t('flight_label')
        : effectiveSubtype === 'walk'
            ? t('walking_label')
            : effectiveSubtype === 'car'
                ? t('car_label')
                : t('travel_label');
    const travelTooltipParts = [travelLabel];
    if (distanceInfo?.distance) travelTooltipParts.push(distanceInfo.distance);
    if (distanceInfo?.duration) travelTooltipParts.push(distanceInfo.duration);
    if (effectiveSubtype === 'flight') travelTooltipParts.push(t('search_flights_expedia', 'Search Flights on Expedia'));
    if (effectiveSubtype === 'car') travelTooltipParts.push('Rent a car on Expedia');
    const travelTooltip = travelTooltipParts.filter(Boolean).join(' • ');
    
    const handleTravelIconClick = (event) => {
        event.stopPropagation();
        
        // For flights, open Expedia affiliate link
        if (effectiveSubtype === 'flight') {
            console.log('handleTravelIconClick - flight detected');
            console.log('handleTravelIconClick - trip:', trip);
            console.log('handleTravelIconClick - trip?.start_date:', trip?.start_date);
            console.log('handleTravelIconClick - trip?.end_date:', trip?.end_date);
            console.log('handleTravelIconClick - isTripFlexible:', isTripFlexible);
            
            if (isTripFlexible && onFlightUpdateDisabled) {
                console.log('handleTravelIconClick - trip is flexible, showing disabled message');
                onFlightUpdateDisabled();
                return;
            }
            // Inner travel segments are one-way flights with the activity day date
            const expediaLink = generateExpediaFlightLink(activity, trip?.start_date, trip?.end_date, dayDate, false);
            console.log('handleTravelIconClick - expediaLink:', expediaLink);
            if (expediaLink) {
                console.log('handleTravelIconClick - opening Expedia link');
                window.open(expediaLink, '_blank');
            } else {
                console.log('handleTravelIconClick - no link generated, falling back to modal');
                // Fallback to update flight modal if link cannot be generated
                onFlightUpdate && onFlightUpdate(activity);
            }
            return;
        }
        
        // For car rentals, open Expedia car rental link
        if (effectiveSubtype === 'car') {
            console.log('handleTravelIconClick - car rental detected');
            const carRentalLink = generateExpediaCarRentalLink(activity, dayDate);
            if (carRentalLink) {
                console.log('handleTravelIconClick - opening car rental link');
                window.open(carRentalLink, '_blank');
            }
            return;
        }
        
        // For other travel types, open the transportation modal
        if (!onOpenTransportation) {
            return;
        }
        const travelMatch = activity.title?.match(/Travel from (.+?) to (.+)/i);
        const destination = travelMatch ? (travelMatch[2]?.trim() || '') : '';
        onOpenTransportation(dayLabel, destination, dayDate);
    };

    const flightIconColor = hasTransportationInfo ? '#10b981' : '#ef4444';
    const flightHoverColor = hasTransportationInfo ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)';
    const travelIconColor = effectiveSubtype === 'flight'
        ? flightIconColor
        : effectiveSubtype === 'walk'
            ? '#4caf50'
            : effectiveSubtype === 'car'
                ? '#2196f3'
                : '#616161';
    const travelIconElement = (() => {
        if (effectiveSubtype === 'flight') {
            return <FlightTakeoffIcon />;
        }
        if (effectiveSubtype === 'walk') {
            return <DirectionsWalkIcon />;
        }
        if (effectiveSubtype === 'car') {
            return <DirectionsCarIcon />;
        }
        return <MapIcon />;
    })();
    
    // Status dot colors: red for errors, orange for warnings, green for ok
    const statusColor = isLodgingActivity ? '#6b7280' : (isBeforeArrival || previousOverlap) ? '#ef4444' : (overlap ? '#f59e0b' : '#10b981');

    // Helper to get Viator price tier
    const getViatorPriceTier = (price) => {
        if (!price) return null;
        const p = Number(price);
        if (p < 30) return '$';
        if (p < 80) return '$$';
        return '$$$';
    };
    const viatorPriceTier = getViatorPriceTier(activity.viator_price);

    // Traveller's own cost for this item (from import / email parsing).
    const costInfo = getActivityCostInfo(activity);

    return (
        <Box
            onClick={handleActivityClick}
            sx={{
                position: 'relative',
                p: { xs: 1, sm: 1.5 },
                mb: 1,
                border: '1px solid',
                borderColor: (overlap || isBeforeArrival || previousOverlap) ? 'warning.light' : 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                bgcolor: isDragging ? '#e3f2fd' : (activity.email_import_pending == 1 ? '#fff8e1' : (activity.is_ai_suggestion ? '#fafbfc' : 'white')),
                boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                overflow: 'hidden',
                width: '100%',
                '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'primary.light',
                    '& .drag-handle': { opacity: 0.7 }
                },
            }}
        >
            {/* TOP ROW: Time on left, Icons/badges/menu aligned right */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                flexWrap: 'nowrap',
                gap: 1
            }}>
                {/* Left side: Drag handle + Time in one row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {/* Drag Handle - hidden on mobile */}
                    <Box 
                        className="drag-handle"
                        {...dragHandleProps}
                        sx={{ 
                            cursor: 'grab',
                            color: 'text.disabled',
                            opacity: 0,
                            transition: 'opacity 0.15s',
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            '&:active': { cursor: 'grabbing' }
                        }}
                    >
                        <DragIndicatorIcon fontSize="small" />
                    </Box>
                    
                    {/* Time display - horizontal single row */}
                    <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        pr: 1
                    }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.primary',
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {activity.start_time ? activity.start_time.slice(0,5) : '--:--'}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'text.secondary',
                                fontSize: { xs: '0.7rem', sm: '0.8rem' }
                            }}
                        >
                            -
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: overlap ? 'error.main' : 'text.secondary',
                                fontWeight: overlap ? 600 : 400,
                                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {endTime}{endsNextDay && endTime !== '--:--' ? ' +1' : ''}
                        </Typography>
                    </Box>
                </Box>
                
                {/* Right side: Icons | Viator Price | Menu - with 20px gaps between groups */}
                <Box 
                    onClick={(e) => e.stopPropagation()}
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        flexWrap: 'nowrap',
                        flexShrink: 0
                    }}
                >
                    {/* GROUP 1: Other icons (left side) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Pinned indicator */}
                        {activity.is_pinned == 1 && (
                            <PushPinIcon sx={{ fontSize: '14px', color: 'warning.main', transform: 'rotate(45deg)' }} />
                        )}

                        {/* Email-imported indicator: click to open edit/confirm modal */}
                        {activity.email_import_pending == 1 && (
                            <Tooltip title="Added from email – click to review & confirm" arrow>
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); onEdit(activity); }}
                                    sx={{ p: 0.25, color: 'info.main' }}
                                >
                                    <MarkEmailReadIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {/* View source email link — shown regardless of pending status */}
                        {activity.source_message_id && (
                            <Tooltip title="View source email" arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleViewSourceEmail}
                                    disabled={loadingSourceEmail}
                                    sx={{ p: 0.25, color: 'text.secondary' }}
                                >
                                    {loadingSourceEmail
                                        ? <CircularProgress size={12} />
                                        : <OpenInNewIcon sx={{ fontSize: '13px' }} />
                                    }
                                </IconButton>
                            </Tooltip>
                        )}
                        
                        {/* Warning indicator */}
                        {(overlap || isBeforeArrival || previousOverlap) && (
                            <WarningAmberIcon 
                                sx={{ 
                                    fontSize: '16px', 
                                    color: isBeforeArrival || previousOverlap ? 'error.main' : 'warning.main',
                                    cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (overlap) setShowOverlapError(prev => !prev);
                                    if (isBeforeArrival) setShowBeforeArrivalError(prev => !prev);
                                    if (previousOverlap) setShowPreviousOverlapError(prev => !prev);
                                }}
                            />
                        )}
                        
                        {/* Flight icon for flights - opens Expedia affiliate link */}
                        {activity.type === 'flight' && (
                            <Tooltip title={isTripFlexible ? t('set_dates_first') : t('search_flights_expedia', 'Search Flights on Expedia')} arrow>
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isTripFlexible) {
                                                onFlightUpdateDisabled && onFlightUpdateDisabled();
                                                return;
                                            }
                                            // First/last day flights are roundtrip with trip start/end dates
                                            const expediaLink = generateExpediaFlightLink(activity, trip?.start_date, trip?.end_date, null, true);
                                            if (expediaLink) {
                                                window.open(expediaLink, '_blank');
                                            } else {
                                                // Fallback to update flight modal if link cannot be generated
                                                onFlightUpdate && onFlightUpdate(activity);
                                            }
                                        }}
                                        size="small"
                                        disabled={isTripFlexible}
                                        sx={{ p: 0.25, color: activity.reference_ticket_no ? 'success.main' : 'primary.main' }}
                                    >
                                        <FlightTakeoffIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                    {/* Check Price button or loading indicator */}
                                    {loadingAmadeusCost && (
                                        <span style={{ fontSize: '0.6rem', fontWeight: 400, color: '#999' }}>
                                            ...
                                        </span>
                                    )}
                                    {!loadingAmadeusCost && !isTripFlexible && !amadeusFlightCost && !flightFetchAttempted && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const expediaLink = generateExpediaFlightLink(activity, trip?.start_date, trip?.end_date, null, true);
                                                if (expediaLink) window.open(expediaLink, '_blank');
                                            }}
                                            style={{
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                color: '#0066cc',
                                                background: 'none',
                                                border: 'none',
                                                padding: '0px',
                                                cursor: 'pointer',
                                                textDecoration: 'underline'
                                            }}
                                        >
                                            Check Price
                                        </button>
                                    )}
                                    {/* Price range displayed below button */}
                                    {amadeusFlightCost && typeof amadeusFlightCost === 'string' && !loadingAmadeusCost && (
                                        <span style={{ fontSize: '0.6rem', fontWeight: 500, color: '#333' }}>
                                            {amadeusFlightCost}
                                        </span>
                                    )}
                                </span>
                            </Tooltip>
                        )}
                        
                        {/* Travel segment icon with price below */}
                        {isTravelSegment && (
                            <Tooltip title={travelTooltip} arrow>
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    <IconButton
                                        onClick={handleTravelIconClick}
                                        size="small"
                                        sx={{ p: 0.25, color: travelIconColor }}
                                    >
                                        {travelIconElement}
                                    </IconButton>
                                    {/* Price link below icon - only show for flights, not for cars */}
                                    {effectiveSubtype === 'flight' && (
                                        <>
                                            {amadeusFlightCost && typeof amadeusFlightCost === 'string' && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTravelIconClick(e);
                                                    }}
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        color: '#0066cc',
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: '0px',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    {amadeusFlightCost}
                                                </button>
                                            )}
                                            {loadingAmadeusCost && (
                                                <span style={{ fontSize: '0.6rem', fontWeight: 400, color: '#999' }}>
                                                    ...
                                                </span>
                                            )}
                                            {!amadeusFlightCost && !loadingAmadeusCost && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTravelIconClick(e);
                                                    }}
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        color: '#0066cc',
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: '0px',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    Check Price
                                                </button>
                                            )}
                                        </>
                                    )}
                                </span>
                            </Tooltip>
                        )}
                        
                        {/* Booking status badge */}
                        {(activity.reference_ticket_no || activity.booking_agency) && (
                            <Chip 
                                icon={<CheckCircleIcon sx={{ fontSize: '10px !important' }} />}
                                label={t('booked')}
                                size="small"
                                sx={{ 
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: '20px',
                                    '& .MuiChip-icon': { color: 'white', ml: '2px' }
                                }}
                            />
                        )}
                    </Box>
                    
                    {/* GROUP 2: Viator price tier - 20px gap from icons */}
                    {viatorPriceTier && !activity.reference_ticket_no && !activity.booking_agency && !isTravelSegment && (
                        <Chip 
                            label={viatorPriceTier}
                            size="small"
                            onClick={async (e) => {
                                e.stopPropagation();
                                if (activity.viator_link) {
                                    window.open(activity.viator_link, '_blank');
                                    return;
                                }
                                try {
                                    const token = localStorage.getItem('token');
                                    const response = await fetch(`${API_PATH}/viator_full_details.php`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ activity_id: activity.activity_id, product_code: activity.viator_product_code, language: i18n.language })
                                    });
                                    if (response.ok) {
                                        const data = await response.json();
                                        if (data.success && data.viator_link) window.open(data.viator_link, '_blank');
                                    }
                                } catch (error) {
                                    console.error('Error fetching Viator link:', error);
                                }
                            }}
                            sx={{ 
                                ml: '20px',
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: '20px',
                                cursor: 'pointer',
                                border: '1px solid #c8e6c9',
                                '&:hover': { bgcolor: '#c8e6c9' }
                            }}
                        />
                    )}
                    
                    {/* GROUP 3: Menu button - 20px gap, far right */}
                    <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e);
                    }} sx={{ p: 0.25, ml: '20px' }}>
                        <MoreVertIcon sx={{ fontSize: '18px', color: 'text.secondary' }} />
                    </IconButton>
                </Box>
            </Box>
            
            {/* BOTTOM ROW: Activity name with text wrap, location, and thumbnail */}
            <Box sx={{ width: '100%', pl: { xs: 0, sm: 3 }, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: 500, 
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                            color: 'text.primary',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4
                        }}
                    >
                        {getActivityDisplayTitle(activity)}
                    </Typography>
                    
                    {/* Location - show on all screens now */}
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            display: 'block',
                            mt: 0.25,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                        }}
                    >
                        {locationLabel}
                        {isTravelSegment && distanceInfo?.distance && (
                            <> • {distanceInfo.distance}{distanceInfo.duration ? ` (${distanceInfo.duration})` : ''}</>
                        )}
                    </Typography>

                    {/* Price / cost — shown when captured from import or email parsing */}
                    {costInfo && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.25,
                                mt: 0.5,
                                px: 0.75,
                                py: 0.125,
                                borderRadius: 1,
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                fontWeight: 700,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                lineHeight: 1.5,
                                width: 'fit-content',
                            }}
                        >
                            {costInfo}
                        </Typography>
                    )}

                    {/* Error messages */}
                    {(showOverlapError || showBeforeArrivalError || showPreviousOverlapError) && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                            {showOverlapError && t('overlap_next_error')}
                            {showBeforeArrivalError && t('before_arrival_error', { time: arrivalFlightActivity?.start_time?.slice(0,5) || 'arrival', day: arrivalFlightActivity?.day })}
                            {showPreviousOverlapError && t('previous_overlap_error')}
                        </Typography>
                    )}
                </Box>

                {/* Thumbnail - only for regular activities */}
                {thumbnailUrl && !isLodgingActivity && !isTravelSegment && activity.type !== 'flight' && activity.type !== 'transportation' && (
                    <Box
                        component="img"
                        src={thumbnailUrl}
                        alt={activity.title}
                        sx={{
                            flexShrink: 0,
                            width: { xs: 52, sm: 64 },
                            height: { xs: 52, sm: 64 },
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                )}
            </Box>
            
            {/* Menu - keep outside the layout */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleInfo();
                }}>
                    <InfoOutlinedIcon sx={{ fontSize: '18px', mr: 1, color: 'primary.main' }} />
                    {t('details_menu')}
                </MenuItem>
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                }}>
                    <EditOutlinedIcon sx={{ fontSize: '18px', mr: 1, color: 'primary.main' }} />
                    {t('edit_menu')}
                </MenuItem>
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    handlePin();
                }}>
                    {activity.is_pinned ? (
                        <>
                            <PushPinIcon sx={{ fontSize: '18px', mr: 1, color: 'warning.main' }} />
                            {t('unpin_menu') || 'Unpin Activity'}
                        </>
                    ) : (
                        <>
                            <PushPinOutlinedIcon sx={{ fontSize: '18px', mr: 1, color: 'text.secondary' }} />
                            {t('pin_menu') || 'Pin Activity'}
                        </>
                    )}
                </MenuItem>
                {activity.type !== 'flight' && (
                    <MenuItem onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClose();
                        if (onAskAI) onAskAI(activity);
                    }}>
                        <AIIcon sx={{ fontSize: '18px', mr: 1, color: 'primary.main' }} />
                        {t('ask_ai_menu')}
                    </MenuItem>
                )}
                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                }}>
                    <DeleteOutlineIcon sx={{ fontSize: '18px', mr: 1, color: 'error.main' }} />
                    {t('delete_menu')}
                </MenuItem>
            </Menu>

            {/* Source email viewer dialog */}
            <Dialog
                open={Boolean(sourceEmailMsg)}
                onClose={() => setSourceEmailMsg(null)}
                fullScreen
                sx={{ zIndex: 1400 }}
                PaperProps={{ style: { backgroundColor: '#1a1d2e', color: '#f0f0f5' } }}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle
                    style={{ backgroundColor: '#242740', borderBottom: '1px solid rgba(255,255,255,0.10)' }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1.5 }}
                >
                    <MarkEmailReadIcon sx={{ flexShrink: 0, color: '#6C63FF' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap style={{ color: '#f0f0f5' }}>
                            {sourceEmailMsg?.subject || '(no subject)'}
                        </Typography>
                        <Typography variant="caption" display="block" style={{ color: 'rgba(240,240,245,0.55)' }}>
                            From: {sourceEmailMsg?.sender_email}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setSourceEmailMsg(null)} size="small" sx={{ color: 'rgba(240,240,245,0.55)', flexShrink: 0 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }} style={{ backgroundColor: '#1a1d2e' }}>
                    <Box sx={{ p: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.7 }} style={{ color: '#f0f0f5' }}>
                        {sourceEmailMsg?.body || ''}
                    </Box>
                </DialogContent>
                <DialogActions style={{ backgroundColor: '#242740', borderTop: '1px solid rgba(255,255,255,0.10)' }}>
                    <Button onClick={() => setSourceEmailMsg(null)} variant="contained" sx={{ bgcolor: '#6C63FF', color: '#fff', '&:hover': { bgcolor: '#574fd6' }, fontWeight: 600 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const ItineraryPage = ({ user, isAiWorking, setIsAiWorking, handleShowAiMessage }) => {
    const { t, i18n } = useTranslation();
    const { tripId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [flightSuggestions, setFlightSuggestions] = useState(null);
    const [suggestingFlights, setSuggestingFlights] = useState(false);
    const [flightSuggestionError, setFlightSuggestionError] = useState('');
    const [activitiesByDay, setActivitiesByDay] = useState({}); // Stores activities grouped by day
    const [computedTripDays, setComputedTripDays] = useState([]); // Fallback trip days derived from activities when backend missing trip_days
    const [lockedDays, setLockedDays] = useState({}); // Stores lock status for each day
    const [showTransportationModal, setShowTransportationModal] = useState(false);
    const [showAddActivityModal, setShowAddActivityModal] = useState(false);
    const [showEditActivityModal, setShowEditActivityModal] = useState(false);
    const [editActivity, setEditActivity] = useState(null);
    const [selectedDayForTransport, setSelectedDayForTransport] = useState(null);
    const [transportDefaultDate, setTransportDefaultDate] = useState('');
    const [transportDefaultDestination, setTransportDefaultDestination] = useState('');
    const [selectedTransportationRecord, setSelectedTransportationRecord] = useState(null);
    const [transportationDetailsOpen, setTransportationDetailsOpen] = useState(false);
    const [editingTransportationRecord, setEditingTransportationRecord] = useState(null);
    const [dailyTransportation, setDailyTransportation] = useState({}); // Stores transportation details for each day
    const [activityMenuAnchorEl, setActivityMenuAnchorEl] = useState(null); // For add activity menu
    const initialGenerationStarted = useRef(false);
    const generationTriggeredFromEdit = useRef(false);
    const [pricingFlight, setPricingFlight] = useState(false);
    const [pricedFlightOffer, setPricedFlightOffer] = useState(null);
    const [pricingError, setPricingError] = useState('');
    const [selectedDay, setSelectedDay] = useState(null);
    const [showFlexibleTripMessage, setShowFlexibleTripMessage] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [tempStartDate, setTempStartDate] = useState('');
    const [tempDuration, setTempDuration] = useState('');
    const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
    const [aiContext, setAiContext] = useState({ type: 'general', id: null, activity: null });
    const [showMap, setShowMap] = useState(false); // Controls map visibility
    const [showShareModal, setShowShareModal] = useState(false); // Controls share modal visibility
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [hotelDialogOpen, setHotelDialogOpen] = useState(false);
    const [selectedHotelDay, setSelectedHotelDay] = useState(null);
    const [hotelStays, setHotelStays] = useState([]);
    const [selectedHotelStayId, setSelectedHotelStayId] = useState(null);
    const [cityContexts, setCityContexts] = useState({}); // Store city context for each location
    const [expandedCityContexts, setExpandedCityContexts] = useState({}); // Track which city contexts are expanded
    const isTripFlexible = !trip?.start_date || trip?.start_date === '0000-00-00';
    const [linkedReports, setLinkedReports] = useState([]);
    const [expandedReportId, setExpandedReportId] = useState(null);
    const [noExpenseSnack, setNoExpenseSnack] = useState(false);
    const [expenseExchangeRates, setExpenseExchangeRates] = useState(null);

    // Fetch exchange rates for expense total conversion
    useEffect(() => {
        fetch(`${API_PATH}/exchange_rates.php`)
            .then(r => r.json())
            .then(data => { if (data.result === 'success') setExpenseExchangeRates(data.rates); })
            .catch(() => {});
    }, []);

    const generateStayId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };

    useEffect(() => {
        if (!tripId) return;
        const userId = localStorage.getItem('pu_id');
        const userParam = userId ? `&user_id=${userId}` : '';
        fetch(`${API_PATH}/expense_reports.php?action=list${userParam}`)
            .then(r => r.json())
            .then(data => {
                const all = [...(data.owned || []), ...(data.shared || [])];
                setLinkedReports(all.filter(r => String(r.trip_id) === String(tripId)));
            })
            .catch(() => {});
    }, [tripId]);

    const getStayIconColor = (stay, dayDate) => {
        const parsedDate = dayjs(dayDate);
        const isPast = parsedDate.isValid() && parsedDate.isBefore(dayjs(), 'day');
        if (isPast) return 'text.disabled';
        const checkInDate = dayjs(stay.checkInDateTime);
        const checkOutDate = dayjs(stay.checkOutDateTime);
        if (parsedDate.isSame(checkOutDate, 'day')) return 'warning.main';
        return 'success.main';
    };

    const getHotelActionIconColor = (dayDate, hasReservation) => {
        const parsedDate = dayjs(dayDate);
        const isPast = parsedDate.isValid() && parsedDate.isBefore(dayjs(), 'day');
        if (isPast) return 'text.disabled';
        return hasReservation ? 'success.main' : 'error.main';
    };

    const getStayTooltip = (stay, dayDate) => {
        const parsedDate = dayjs(dayDate);
        const checkInDate = dayjs(stay.checkInDateTime);
        const checkOutDate = dayjs(stay.checkOutDateTime);
        let label = t('stay_tooltip_stay');
        if (parsedDate.isSame(checkInDate, 'day')) {
            label = t('stay_tooltip_checkin');
        } else if (parsedDate.isSame(checkOutDate, 'day')) {
            label = t('stay_tooltip_checkout');
        }
        const stayName = stay.hotelName || stay.hotelAddress;
        return stayName ? `${label}: ${stayName}` : label;
    };

    const staysByDate = useMemo(() => {
        const map = {};
        hotelStays.forEach((stay) => {
            const checkIn = dayjs(stay.checkInDateTime);
            const checkOut = dayjs(stay.checkOutDateTime);
            if (!checkIn.isValid() || !checkOut.isValid()) return;
            if (checkOut.isBefore(checkIn, 'day')) return;

            const addEvent = (dateKey, type) => {
                if (!map[dateKey]) map[dateKey] = [];
                map[dateKey].push({ stayId: stay.id, stay, type });
            };

            addEvent(checkIn.format('YYYY-MM-DD'), 'checkin');
            let current = checkIn.add(1, 'day');
            while (current.isBefore(checkOut, 'day')) {
                addEvent(current.format('YYYY-MM-DD'), 'stay');
                current = current.add(1, 'day');
            }
            addEvent(checkOut.format('YYYY-MM-DD'), 'checkout');
        });
        return map;
    }, [hotelStays]);

    const selectedHotelStay = useMemo(() => {
        return hotelStays.find((stay) => stay.id === selectedHotelStayId) || null;
    }, [hotelStays, selectedHotelStayId]);

    const tripTitle = useMemo(() => {
        if (!trip) return t('trip_default_title');
        
        // Collect all unique countries from activities in chronological order
        const countries = [];
        const seenCountries = new Set();
        
        // Sort days to ensure chronological order
        const sortedDays = Object.keys(activitiesByDay).sort((a, b) => {
            if (a === 'unscheduled') return 1;
            if (b === 'unscheduled') return -1;
            return new Date(a) - new Date(b);
        });

        sortedDays.forEach(dayKey => {
            const dayActivities = activitiesByDay[dayKey];
            dayActivities.forEach(activity => {
                let country = null;
                
                // Try to extract country from location string
                if (activity.location) {
                    const parts = activity.location.split(',').map(p => p.trim());
                    // Check if the last part is a known country
                    const potentialCountry = parts[parts.length - 1];
                    const isKnownCountry = countriesCities.some(c => c.country === potentialCountry);
                    
                    if (isKnownCountry) {
                        country = potentialCountry;
                    } else {
                        // Try finding by city
                        country = findCountryByCity(parts[0]);
                    }
                }
                
                if (country && !seenCountries.has(country)) {
                    seenCountries.add(country);
                    countries.push(country);
                }
            });
        });
        
        if (countries.length > 0) {
             return countries.join(", ");
        }
        
        return trip.name || t('trip_default_title');
    }, [trip, activitiesByDay, t]);

    // ...removed drag-and-drop sensors...

    const fetchTripData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError(t('auth_token_missing'));
                setLoading(false);
                return;
            }

            // Fetch trip details
            const tripResponse = await fetch(`${API_PATH}/trips/get_single.php?trip_id=${tripId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const tripData = await tripResponse.json();
            if (!tripResponse.ok) {
                // If trip not found (404), redirect to home page
                if (tripResponse.status === 404) {
                    console.log('Trip not found, redirecting to home');
                    navigate('/');
                    return;
                }
                setError(tripData.message || t('fetch_trip_details_error'));
                setLoading(false);
                return;
            }
            setTrip(tripData);
            
            console.error('[TRIP DATA] Full trip object received:', {
                'tripData_keys': Object.keys(tripData),
                'tripData_full': tripData,
                'start_location': tripData?.start_location,
                'end_location': tripData?.end_location,
                'destination': tripData?.destination,
                'origin': tripData?.origin,
                'location': tripData?.location,
                'city': tripData?.city,
                'country': tripData?.country
            });

            // Calculate trip days
            const tripDays = [];
            if (tripData.start_date && tripData.start_date !== '0000-00-00' && tripData.end_date && tripData.end_date !== '0000-00-00') {
                const startDate = new Date(tripData.start_date);
                const endDate = new Date(tripData.end_date);
                let currentDate = new Date(startDate);
                let dayCount = 1;
                while (currentDate <= endDate) {
                    tripDays.push({
                        label: currentDate.toLocaleDateString(), // Solid date
                        date: currentDate.toISOString().split('T')[0],
                        dayNumber: dayCount
                    });
                    currentDate.setDate(currentDate.getDate() + 1);
                    dayCount++;
                }
            } else if (tripData.duration_days) {
                for (let i = 1; i <= tripData.duration_days; i++) {
                    tripDays.push({
                        label: `Day ${i}`,
                        date: `day-${i}`, // Placeholder for flexible trips
                        dayNumber: i
                    });
                }
            }

            // Fetch all activities for the trip
            const activitiesResponse = await fetch(`${API_PATH}/activities.php?trip_id=${tripId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const allActivities = await activitiesResponse.json();
            console.log('allActivities', allActivities);
            if (!activitiesResponse.ok) {
                setError(allActivities.message || t('fetch_activities_error'));
                setLoading(false);
                return;
            }

            // Create a map from day number to date string
            const dayNumberToDate = {};
            tripDays.forEach(day => {
                dayNumberToDate[day.dayNumber] = day.date;
            });

            // If tripDays are empty (backend didn't provide start/end or flexible fields), derive from activities numeric day values
            if (tripDays.length === 0) {
                const numericDays = allActivities
                    .map(a => Number(a.day))
                    .filter(n => !isNaN(n) && n > 0);
                if (numericDays.length > 0) {
                    const maxDay = Math.max(...numericDays);
                    for (let i = 1; i <= maxDay; i++) {
                        tripDays.push({ label: `Day ${i}`, date: `day-${i}`, dayNumber: i });
                    }
                    // rebuild dayNumberToDate
                    tripDays.forEach(day => { dayNumberToDate[day.dayNumber] = day.date; });
                    // persist derived days so render will show them
                    setComputedTripDays(tripDays);
                }
            } else {
                // if backend provided tripDays, prefer them
                setComputedTripDays(tripDays);
            }

            // Helper: normalize various date formats into YYYY-MM-DD or return null
            const normalizeToISODate = (val) => {
                if (!val && val !== 0) return null;
                // If already a Date object
                if (val instanceof Date && !isNaN(val.getTime())) return val.toISOString().split('T')[0];

                // If numeric (day number), let caller handle numeric case
                if (typeof val === 'number') return null;

                if (typeof val === 'string') {
                    const s = val.trim();
                    // ISO-ish strings (with or without time)
                    const d1 = new Date(s);
                    if (!isNaN(d1.getTime())) return d1.toISOString().split('T')[0];

                    // Try common dd/mm/YYYY or dd-mm-YYYY
                    const dm1 = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
                    if (dm1) {
                        const day = parseInt(dm1[1], 10);
                        const month = parseInt(dm1[2], 10);
                        const year = parseInt(dm1[3], 10);
                        // Construct as year-month-day
                        const d2 = new Date(year, month - 1, day);
                        if (!isNaN(d2.getTime())) return d2.toISOString().split('T')[0];
                    }

                    // Try YYYYMMDD or YYYY-MM-DD without separators handled above
                    const ymd = s.match(/^(\d{4})[\-]?(\d{2})[\-]?(\d{2})$/);
                    if (ymd) {
                        const year = parseInt(ymd[1], 10);
                        const month = parseInt(ymd[2], 10);
                        const day = parseInt(ymd[3], 10);
                        const d3 = new Date(year, month - 1, day);
                        if (!isNaN(d3.getTime())) return d3.toISOString().split('T')[0];
                    }
                }

                return null;
            };

            // Group activities by day with robust matching and normalization
            const groupedActivities = {};
            const unmapped = [];
            allActivities.forEach(activity => {
                let dateKey = null;

                // 1) If backend provides explicit day_date use it (normalize first)
                if (activity.day_date) {
                    const norm = normalizeToISODate(activity.day_date);
                    if (norm && tripDays.find(d => d.date === norm)) dateKey = norm;
                }

                // 2) If activity.day is numeric, map via dayNumberToDate
                if (!dateKey && (activity.day !== undefined && activity.day !== null)) {
                    const numericDay = Number(activity.day);
                    if (!isNaN(numericDay) && dayNumberToDate[numericDay]) {
                        dateKey = dayNumberToDate[numericDay];
                    }
                }

                // 3) If activity.day looks like a date string, try to normalize and match
                if (!dateKey && typeof activity.day === 'string') {
                    const norm2 = normalizeToISODate(activity.day);
                    if (norm2 && tripDays.find(d => d.date === norm2)) dateKey = norm2;
                }

                // 4) If still not found, attempt to match by label (e.g., 'Day 1')
                if (!dateKey && typeof activity.day === 'string') {
                    const matchByLabel = tripDays.find(d => d.label === activity.day);
                    if (matchByLabel) dateKey = matchByLabel.date;
                }

                if (dateKey) {
                    if (!groupedActivities[dateKey]) groupedActivities[dateKey] = [];
                    groupedActivities[dateKey].push(activity);
                } else {
                    console.warn(t('unmapped_activity_warning'), activity);
                    unmapped.push(activity);
                }
            });

            if (unmapped.length > 0) groupedActivities['unscheduled'] = unmapped;
            setActivitiesByDay(groupedActivities);

            // Fetch locked days
            const lockedDaysResponse = await fetch(`${API_PATH}/days/get_locked.php?trip_id=${tripId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const lockedDaysData = await lockedDaysResponse.json();
            if (lockedDaysResponse.ok) {
                const lockedDaysMap = {};
                lockedDaysData.forEach(day => { lockedDaysMap[day.day_date] = day.is_locked; });
                setLockedDays(lockedDaysMap);
            }

            // Fetch lodging/hotel reservations
            try {
                const lodgingResponse = await fetch(`${API_PATH}/lodging.php?trip_id=${tripId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const lodgingData = await lodgingResponse.json();
                if (lodgingResponse.ok && lodgingData.success && lodgingData.lodgings) {
                    // Convert backend format to frontend format
                    const hotelStaysFromDB = lodgingData.lodgings.map(l => ({
                        id: l.id,
                        dayNumber: l.dayNumber,
                        dayLabel: tripDays.find(d => d.dayNumber === l.dayNumber)?.label || `Day ${l.dayNumber}`,
                        checkInDateTime: `${l.checkInDate}T${l.checkInTime?.substring(0, 5) || '15:00'}`,
                        checkOutDateTime: `${l.checkOutDate}T${l.checkOutTime?.substring(0, 5) || '11:00'}`,
                        hotelName: l.hotelName,
                        hotelAddress: l.hotelAddress,
                        hotelCity: l.hotelCity,
                        hotelCountry: l.hotelCountry,
                        hotelMapPlaceId: l.hotelMapPlaceId,
                        hotelLatitude: l.hotelLatitude,
                        hotelLongitude: l.hotelLongitude,
                        reference: l.reference,
                        notes: l.notes,
                        updatedAt: l.updatedAt
                    }));
                    setHotelStays(hotelStaysFromDB);
                    console.log('Loaded lodging from database:', hotelStaysFromDB);
                }
            } catch (lodgingError) {
                console.error('Error fetching lodging:', lodgingError);
                // Non-critical, continue without lodging data
            }

            // Transportation will be fetched separately in a non-blocking way
            setDailyTransportation({});

        } catch (err) {
            console.error("Error fetching trip data:", err);
            setError(t('fetch_trip_data_error'));
        } finally {
            setLoading(false);
        }
    }, [tripId, t, navigate]);

    // Track which activities we've already attempted to fetch Viator data for
    const [viatorFetchedIds, setViatorFetchedIds] = useState(new Set());

    // Function to fetch Viator pricing for specific activities
    const fetchViatorData = useCallback(async (activities = null) => {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Get all activities from activitiesByDay if not provided
        const allActivities = activities || Object.values(activitiesByDay).flat();
        
        // Only fetch for activities that:
        // 1. Don't have Viator data yet
        // 2. Haven't been attempted in this session
        // 3. Are not flights or FREE activities
        const activitiesNeedingViator = allActivities.filter(activity => 
            !activity.viator_price && 
            !activity.viator_link && 
            !viatorFetchedIds.has(activity.activity_id) && // Don't re-fetch in same session
            activity.location && 
            activity.title &&
            activity.type !== 'flight' && // Skip flights
            (!activity.viator_activity_name || activity.viator_activity_name.toUpperCase() !== 'FREE') // Only skip if explicitly marked FREE
        );

        if (activitiesNeedingViator.length === 0) return;

        console.log(`Fetching Viator data for ${activitiesNeedingViator.length} activities...`);

        // Mark these activities as attempted
        setViatorFetchedIds(prev => {
            const updated = new Set(prev);
            activitiesNeedingViator.forEach(act => updated.add(act.activity_id));
            return updated;
        });

        // Fetch Viator data for each activity (but don't wait for all to complete)
        activitiesNeedingViator.forEach(async (activity) => {
            try {
                const response = await fetch(`${API_PATH}/viator_quick_price.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        activity_id: activity.activity_id,
                        location: activity.location,
                        title: activity.title,
                        viator_activity_type: activity.viator_activity_type,
                        viator_activity_name: activity.viator_activity_name,
                        language: i18n.language
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        console.log(`✓ Viator price fetched for: ${activity.title} - $${data.viator_price} (${data.match_type || data.cached ? 'cached' : 'fresh'} ${data.match_type || ''})`);
                        // Update the activity in state
                        setActivitiesByDay(prev => {
                            const updated = { ...prev };
                            Object.keys(updated).forEach(day => {
                                updated[day] = updated[day].map(act => 
                                    act.activity_id === activity.activity_id
                                        ? { 
                                            ...act, 
                                            viator_price: data.viator_price, 
                                            viator_product_code: data.product_code,
                                            viator_match_type: data.match_type
                                        }
                                        : act
                                );
                            });
                            return updated;
                        });
                    }
                }
            } catch (error) {
                console.error(`Error fetching Viator data for ${activity.title}:`, error);
            }
        });
    }, [activitiesByDay, viatorFetchedIds]);

    // Fetch Viator data ONCE when activities are first loaded
    useEffect(() => {
        if (Object.keys(activitiesByDay).length > 0 && !loading && viatorFetchedIds.size === 0) {
            fetchViatorData();
        }
    }, [activitiesByDay, loading]); // Removed fetchViatorData from dependencies to prevent loop

    // Fetch city contexts when activities are loaded
    useEffect(() => {
        if (Object.keys(activitiesByDay).length > 0 && !loading) {
            fetchCityContexts();
        }
    }, [activitiesByDay, loading, tripId]);

    // Listen for custom event to open transportation modal
    useEffect(() => {
        const handleOpenTransportationModalEvent = (event) => {
            const { dayLabel, destination, date } = event.detail;
            handleOpenTransportationModal(dayLabel, destination, date);
        };

        window.addEventListener('openTransportationModal', handleOpenTransportationModalEvent);

        return () => {
            window.removeEventListener('openTransportationModal', handleOpenTransportationModalEvent);
        };
    }, []);

    // Function to trigger AI itinerary generation (background job system)
    const generateItinerary = useCallback(async () => {
        console.log('🎯 generateItinerary called for trip:', tripId);
        if (!trip) {
            console.log('❌ No trip data, aborting generateItinerary');
            return;
        }
        console.log('✅ Starting itinerary generation in background...');
        handleShowAiMessage(t('starting_generation'));
        setIsAiWorking(true);
        
        const token = localStorage.getItem('token');
        try {
            // Start the background job
            console.log('📡 Creating background generation job...');
            const jobResponse = await fetch(`${API_PATH}/trips/start_generation_job.php?trip_id=${tripId}`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ trip_id: tripId })
            });
            
            if (!jobResponse.ok) {
                const errorData = await jobResponse.json();
                console.error('❌ Failed to start job:', jobResponse.status, errorData);
                setError(errorData.error || t('start_generation_error'));
                setIsAiWorking(false);
                return;
            }
            
            const jobData = await jobResponse.json();
            const jobId = jobData.job_id;
            console.log('✅ Job created:', jobId, '- Polling for progress...');
            handleShowAiMessage(t('generation_in_progress'));
            
            // Add timeout to prevent infinite polling (10 minutes max)
            const maxPollTime = 10 * 60 * 1000; // 10 minutes
            const startTime = Date.now();
            let pollCount = 0;
            
            // Poll job status every 3 seconds
            const pollJobStatus = setInterval(async () => {
                pollCount++;
                const elapsedTime = Date.now() - startTime;
                
                // Timeout after 10 minutes
                if (elapsedTime > maxPollTime) {
                    clearInterval(pollJobStatus);
                    console.error('⏱️ Polling timeout after 10 minutes');
                    setError(t('generation_timeout'));
                    setIsAiWorking(false);
                    return;
                }
                
                try {
                    const statusResponse = await fetch(`${API_PATH}/trips/check_job_status.php?job_id=${jobId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    
                    if (!statusResponse.ok) {
                        clearInterval(pollJobStatus);
                        setError(t('generation_lost_connection'));
                        setIsAiWorking(false);
                        return;
                    }
                    
                    const statusData = await statusResponse.json();
                    const job = statusData.job;
                    
                    console.log(`🔄 Job ${jobId} status: ${job.status} (${job.progress}%) - ${job.current_step} [Poll #${pollCount}]`);
                    
                    // Update UI with progress
                    if (job.current_step) {
                        handleShowAiMessage(`${job.current_step}... (${job.progress || 0}%)`);
                    }
                    
                    // Refresh activities periodically while processing (but not every poll)
                    if (job.status === 'processing' && pollCount % 3 === 0) {
                        fetchTripData();
                    }
                    
                    // Check if job completed
                    if (job.status === 'completed') {
                        clearInterval(pollJobStatus);
                        console.log('✅ Generation completed successfully!');
                        handleShowAiMessage(t('generation_completed'));
                        fetchTripData(); // Final refresh
                        // Fetch city contexts after generation completes
                        setTimeout(() => {
                            fetchCityContexts();
                        }, 500);
                        setTimeout(() => {
                            setIsAiWorking(false);
                        }, 2000);
                    } else if (job.status === 'failed') {
                        clearInterval(pollJobStatus);
                        console.error('❌ Job failed:', job.error_message);
                        setError(job.error_message || t('generation_failed'));
                        setIsAiWorking(false);
                    }
                } catch (pollErr) {
                    console.error('Error polling job status:', pollErr);
                    // Don't stop polling on transient errors, but count failures
                    if (pollCount > 50) { // After ~2.5 minutes of polls
                        clearInterval(pollJobStatus);
                        setError(t('polling_error'));
                        setIsAiWorking(false);
                    }
                }
            }, 3000); // Poll every 3 seconds
            
        } catch (err) {
            console.error("Error generating itinerary:", err);
            setError(t('start_generation_error'));
            setIsAiWorking(false);
        }
    }, [tripId, trip, fetchTripData, setIsAiWorking, handleShowAiMessage, t]);

    // Separate function to fetch transportation data asynchronously (non-blocking)
    const fetchTransportationData = useCallback(async () => {
        const dayLabels = (computedTripDays || [])
            .map(day => day?.date)
            .filter(date => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date));

        if (dayLabels.length === 0) {
            console.warn('[Transport] fetchTransportationData skipped – no concrete day labels', { trip: !!trip, tripDays: computedTripDays?.length || 0 });
            return;
        }
        
        const token = localStorage.getItem('token');
        const fetchedTransportation = {};
        console.log('[Transport] fetching for trip', tripId, 'day labels:', dayLabels);

        for (const dayLabel of dayLabels) {
            try {
                const transportResponse = await fetch(`${API_BASE_URL}/get_transportation.php?trip_id=${tripId}&day_label=${encodeURIComponent(dayLabel)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (transportResponse.ok) {
                    const transportData = await transportResponse.json();
                    if (transportData.length > 0) {
                        console.log('[Transport] found', transportData.length, 'record(s) for', dayLabel);
                    }
                    fetchedTransportation[dayLabel] = transportData;
                } else {
                    console.error(`Failed to fetch transportation for ${dayLabel}:`, await transportResponse.json());
                }
            } catch (transportErr) {
                console.error(`Error fetching transportation for ${dayLabel}:`, transportErr);
            }
        }
        console.log('[Transport] done. keys with data:', Object.keys(fetchedTransportation).filter(k => fetchedTransportation[k]?.length > 0));
        setDailyTransportation(fetchedTransportation);
    }, [tripId, trip, computedTripDays]);

    const handleAddActivity = (dayNumber) => {
        setSelectedDay(dayNumber);
        setShowAddActivityModal(true);
    };

    const handleAddActivityClick = (event, dayNumber) => {
        // Store the anchor element for the menu
        setActivityMenuAnchorEl(event.currentTarget);
        setSelectedDay(dayNumber);
    };

    const handleAddActivityMenuClose = () => {
        setActivityMenuAnchorEl(null);
    };

    const handleAddActivityType = (type) => {
        if (type === 'activity') {
            setShowAddActivityModal(true);
        } else if (type === 'transportation') {
            // Find the selected day from tripDays to get its date
            const selectedDayObj = tripDays.find(day => day.dayNumber === selectedDay);
            const dayDate = selectedDayObj?.date || new Date().toISOString().split('T')[0];
            handleOpenTransportationModal(dayDate, '', dayDate);
        } else if (type === 'lodging') {
            const selectedDayObj = tripDays.find(day => day.dayNumber === selectedDay);
            if (selectedDayObj) {
                handleOpenHotelDialog(selectedDayObj);
            } else {
                setSelectedHotelDay({
                    date: new Date().toISOString().split('T')[0],
                    dayNumber: selectedDay,
                    label: `Day ${selectedDay}`
                });
                setHotelDialogOpen(true);
            }
        }
        handleAddActivityMenuClose();
    };

    const handleEditActivity = (activity) => {
        setEditActivity(activity);
        setShowEditActivityModal(true);
    };

    const handleFlightUpdateClick = (activity) => {
        // Open AddActivityModal in edit mode for the flight activity
        setEditActivity(activity);
        setShowEditActivityModal(true);
    };

    const handleFlightUpdateDisabled = () => {
        setShowFlexibleTripMessage(true);
        setTimeout(() => {
            setShowFlexibleTripMessage(false);
        }, 2000);
    };

    const handleOpenTransportationModal = (dayLabel, destination, defaultDate) => {
        setEditingTransportationRecord(null);
        const normalizedDayLabel = (typeof dayLabel === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dayLabel))
            ? dayLabel
            : (defaultDate || null);
        setSelectedDayForTransport(normalizedDayLabel);
        setTransportDefaultDestination(destination || '');
        setTransportDefaultDate(defaultDate || '');
        setShowTransportationModal(true);
    };

    const handleCloseTransportationModal = () => {
        setShowTransportationModal(false);
        setSelectedDayForTransport(null);
        setTransportDefaultDestination('');
        setTransportDefaultDate('');
        setEditingTransportationRecord(null);
        fetchTransportationData();
    };

    const handleOpenTransportationDetails = (record) => {
        if (!record) return;
        setSelectedTransportationRecord(record);
        setTransportationDetailsOpen(true);
    };

    const handleCloseTransportationDetails = () => {
        setTransportationDetailsOpen(false);
        setSelectedTransportationRecord(null);
    };

    const handleEditTransportationRecord = (record) => {
        if (!record) return;
        setEditingTransportationRecord(record);
        setSelectedDayForTransport(record.day_label || null);
        setTransportDefaultDestination(record.destination_city || '');
        setTransportDefaultDate(record.departure_date || record.day_label || '');
        setShowTransportationModal(true);
        setTransportationDetailsOpen(false);
    };

    const handleDeleteTransportationRecord = async (record) => {
        if (!record?.id) return;
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const response = await fetch(`${API_BASE_URL}/transportation.php`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id: record.id })
            });

            if (response.ok) {
                setDailyTransportation((prev) => {
                    const next = { ...prev };
                    Object.keys(next).forEach((key) => {
                        next[key] = (next[key] || []).filter((item) => item.id !== record.id);
                        if (next[key].length === 0) delete next[key];
                    });
                    return next;
                });
                handleCloseTransportationDetails();
            } else {
                const errData = await response.json().catch(() => ({}));
                setError(errData.message || 'Failed to delete transportation record');
            }
        } catch (err) {
            console.error('Delete transportation error', err);
            setError('Failed to delete transportation record');
        }
    };

    const handleOpenHotelDialog = (day, stayId = null) => {
        // Use route city for this day if available, otherwise fall back to trip start/end location
        const routeDayData = trip?.route?.find(r => r.day === day.dayNumber) || {};
        const fallbackLocation = routeDayData.city
            ? `${routeDayData.city}${routeDayData.country ? ', ' + routeDayData.country : ''}`
            : trip?.end_location || trip?.start_location || '';
        const transportationRecords = dailyTransportation[day.date] || [];
        
        console.error('[HOTEL DIALOG OPEN] ========== DEBUG ==========');
        console.error('[HOTEL DIALOG OPEN] Day selected:', day);
        console.error('[HOTEL DIALOG OPEN] Day date:', day.date);
        console.error('[HOTEL DIALOG OPEN] activitiesByDay keys:', Object.keys(activitiesByDay));
        console.error('[HOTEL DIALOG OPEN] activitiesByDay full:', activitiesByDay);
        console.error('[HOTEL DIALOG OPEN] Activities for this day:', activitiesByDay[day.date]);
        
        if (activitiesByDay[day.date] && activitiesByDay[day.date].length > 0) {
            const lastActivity = activitiesByDay[day.date][activitiesByDay[day.date].length - 1];
            console.error('[HOTEL DIALOG OPEN] Last activity for this day:', {
                'title': lastActivity?.title,
                'location': lastActivity?.location,
                'destination': lastActivity?.destination,
                'day': lastActivity?.day,
                'all_keys': Object.keys(lastActivity || {})
            });
        } else {
            console.error('[HOTEL DIALOG OPEN] NO ACTIVITIES FOUND FOR THIS DAY!');
        }
        
        console.error('[HOTEL DIALOG OPEN] fallbackLocation from trip:', fallbackLocation);
        console.error('[HOTEL DIALOG OPEN] trip object:', trip);
        
        const locationHint = getDayLocationHint(day, activitiesByDay, fallbackLocation, transportationRecords, computedTripDays);
        console.error('[HOTEL DIALOG OPEN] Location hint result:', locationHint);
        setSelectedHotelDay({ ...day, locationHint });
        setSelectedHotelStayId(stayId);
        setHotelDialogOpen(true);
    };

    const handleCloseHotelDialog = () => {
        setHotelDialogOpen(false);
        setSelectedHotelDay(null);
        setSelectedHotelStayId(null);
    };

    const handleSaveHotelReservation = async (reservation) => {
        console.error('[LODGING SAVE] Function called with:', { selectedHotelDay, reservation, selectedHotelStayId });
        
        if (!selectedHotelDay || !reservation) {
            console.error('[LODGING SAVE] Early return - missing data:', { selectedHotelDay, reservation });
            return;
        }
        
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('[LODGING SAVE] No auth token found');
            return;
        }

        try {
            const isUpdate = selectedHotelStayId && typeof selectedHotelStayId === 'number';
            const method = isUpdate ? 'PUT' : 'POST';
            
            // Parse check-in/out datetime from reservation
            const checkInParts = reservation.checkInDateTime?.split('T') || [];
            const checkOutParts = reservation.checkOutDateTime?.split('T') || [];
            
            const payload = {
                ...(isUpdate ? { id: selectedHotelStayId } : {}),
                trip_id: parseInt(tripId),
                dayNumber: selectedHotelDay.dayNumber,
                checkInDate: checkInParts[0] || reservation.checkInDate,
                checkOutDate: checkOutParts[0] || reservation.checkOutDate,
                checkInTime: checkInParts[1] || '15:00',
                checkOutTime: checkOutParts[1] || '11:00',
                hotelName: reservation.hotelName || null,
                hotelAddress: reservation.hotelAddress || null,
                hotelCity: reservation.hotelCity || null,
                hotelCountry: reservation.hotelCountry || null,
                hotelMapPlaceId: reservation.hotelMapPlaceId || null,
                hotelLatitude: reservation.hotelLatitude || null,
                hotelLongitude: reservation.hotelLongitude || null,
                reference: reservation.reference || null,
                notes: reservation.notes || null
            };

            console.log('Saving lodging:', method, payload);

            const response = await fetch(`${API_PATH}/lodging.php`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            
            if (!response.ok || !result.success) {
                console.error('Failed to save lodging:', result);
                throw new Error(result.message || 'Failed to save lodging');
            }

            // Update local state with the saved data
            const stayPayload = {
                id: isUpdate ? selectedHotelStayId : result.id,
                ...reservation,
                dayLabel: selectedHotelDay.label,
                dayNumber: selectedHotelDay.dayNumber,
                updatedAt: new Date().toISOString()
            };

            setHotelStays((prev) => {
                if (isUpdate) {
                    return prev.map((stay) => (stay.id === selectedHotelStayId ? stayPayload : stay));
                }
                return [...prev, stayPayload];
            });

            handleCloseHotelDialog();
        } catch (error) {
            console.error('Error saving lodging:', error);
            // Optionally show error to user
        }
    };

    const handleDeleteHotelStay = async (stayId) => {
        if (!stayId) return;
        
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('No auth token found');
            return;
        }

        try {
            // Only call API if it's a real database ID (number), not a temp client ID
            if (typeof stayId === 'number') {
                const response = await fetch(`${API_PATH}/lodging.php?id=${stayId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const result = await response.json();
                
                if (!response.ok || !result.success) {
                    console.error('Failed to delete lodging:', result);
                    throw new Error(result.message || 'Failed to delete lodging');
                }
            }

            setHotelStays((prev) => prev.filter((stay) => stay.id !== stayId));
            handleCloseHotelDialog();
        } catch (error) {
            console.error('Error deleting lodging:', error);
            // Optionally show error to user
        }
    };

    // AI Assistant modal callbacks
    const handleAIOpenAddFlightModal = (day) => {
        setSelectedDay(day || null);
        setEditActivity(null);
        setShowEditActivityModal(false);
        setShowAddActivityModal(true);
    };

    const handleAIOpenAddActivityModal = (day) => {
        setSelectedDay(day || null);
        setShowAddActivityModal(true);
    };

    const handleAIOpenEditActivityModal = (activityId) => {
        // Find the activity by ID
        const activity = Object.values(activities).flat().find(a => a.activity_id === activityId);
        if (activity) {
            setEditActivity(activity);
            setShowEditActivityModal(true);
        }
    };

    const handleOpenDatePicker = () => {
        setTempDuration(trip?.duration_days || 5);
        setShowDatePicker(true);
    };

    const handleCloseDatePicker = () => {
        setShowDatePicker(false);
        setTempStartDate('');
        setTempDuration('');
    };

    const handleSaveTripDates = async () => {
        if (!tempStartDate || !tempDuration) {
            alert(t('select_date_duration_error'));
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const oldDuration = trip?.duration_days || 0;
            const newDuration = parseInt(tempDuration);
            
            const response = await fetch(`${API_PATH}/trips/update_trip_dates.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trip_id: tripId,
                    start_date: tempStartDate,
                    duration_days: newDuration
                })
            });

            if (response.ok) {
                handleCloseDatePicker();
                await fetchTripData(); // Reload trip data
                
                // If duration increased, generate itinerary for the new days
                if (newDuration > oldDuration) {
                    console.log('🎯 Duration increased from', oldDuration, 'to', newDuration, '- generating itinerary for new days');
                    // Small delay to ensure trip data is loaded
                    setTimeout(() => {
                        generateItinerary();
                    }, 500);
                }
            } else {
                const errorData = await response.json();
                alert(t('update_dates_failed') + (errorData.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error updating trip dates:', error);
            alert(t('update_dates_error'));
        }
    };

    const handleCloseAddActivityModal = () => {
        setShowAddActivityModal(false);
        fetchTripData(); // Refetch data
    }

    const handleCloseEditActivityModal = () => {
        setShowEditActivityModal(false);
        setEditActivity(null);
        fetchTripData();
    }

    const handleDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        console.log('🎯 Drag ended:', { source, destination, draggableId });

        // Dropped outside a valid droppable
        if (!destination) {
            console.log('❌ No destination - dropped outside');
            return;
        }

        // No movement
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            console.log('❌ No movement detected');
            return;
        }

        // Extract activity ID from draggableId (format: "activity-123")
        const activityId = parseInt(draggableId.replace('activity-', ''));
        
        // Extract target day number from droppableId (format: "day-1")
        const targetDay = parseInt(destination.droppableId.replace('day-', ''));

        console.log('📤 Calling reorder API:', {
            activity_id: activityId,
            target_day: targetDay,
            target_index: destination.index,
            trip_id: tripId
        });

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/reorder_activities.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    activity_id: activityId,
                    target_day: targetDay,
                    target_index: destination.index,
                    trip_id: tripId
                })
            });

            console.log('📥 Response status:', response.status);
            const responseData = await response.json();
            console.log('📥 Response data:', responseData);

            if (!response.ok) {
                throw new Error(responseData.message || 'Failed to reorder activity');
            }

            // Refresh trip data to show updated times
            console.log('🔄 Refreshing trip data...');
            await fetchTripData();
            console.log('✅ Trip data refreshed');
        } catch (error) {
            console.error('❌ Error reordering activity:', error);
            alert(`Failed to reorder activity: ${error.message}`);
        }
    };

    useEffect(() => {
        fetchTripData();
    }, [fetchTripData]);

    // Force itinerary generation on component mount (disabled temporarily to fix React error)
    // useEffect(() => {
    //     if (!forceGenerationAttempted.current && trip && !loading) {
    //         const forceGenerationTimer = setTimeout(() => {
    //             console.log('🔄 Force generating itinerary after component mount delay');
    //             forceGenerationAttempted.current = true;
    //             generateItinerary();
    //         }, 2000);

    //         return () => clearTimeout(forceGenerationTimer);
    //     }
    // }, [trip, loading, generateItinerary]);

    // Fetch transportation data separately (non-blocking)
    useEffect(() => {
        if (trip && !loading) {
            fetchTransportationData();
        }
    }, [trip, loading, fetchTransportationData]);

    // Reset the generation flag when tripId changes (new trip or page reload)
    useEffect(() => {
        console.log('🔄 Resetting initialGenerationStarted for trip:', tripId);
        initialGenerationStarted.current = false;
    }, [tripId]);

    // Function to fetch city contexts from trip_days
    const fetchCityContexts = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token || !tripId) return;
            
            const response = await fetch(`${API_PATH}/trips/get_city_contexts.php?trip_id=${tripId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data && typeof data === 'object') {
                    setCityContexts(data);
                    console.log('✅ City contexts loaded:', data);
                    // Also log a sample for debugging
                    const sample = Object.entries(data)[0];
                    if (sample) {
                        console.log('✅ Sample city context:', { location: sample[0], context: sample[1] });
                    }
                }
            } else {
                console.log('Failed to fetch city contexts, status:', response.status);
                const errorText = await response.text();
                console.log('Error response:', errorText);
            }
        } catch (error) {
            console.log('City contexts not available:', error.message);
            // Silently fail - city contexts are optional
        }
    }, [tripId]);

    // Fetch trip data on mount
    useEffect(() => {
        fetchTripData();
    }, [fetchTripData]);
    
    // State for interactive activity builder (replaces auto-generation)
    const [showInteractiveBuilder, setShowInteractiveBuilder] = useState(false);
    const [suggestActivitiesForDay, setSuggestActivitiesForDay] = useState(null); // { dayNumber, dayIndex, city, country }

    // Handle generation trigger from EditTripForm (when duration changed)
    useEffect(() => {
        if (location.state?.triggerGeneration && !generationTriggeredFromEdit.current && trip && !loading) {
            console.log('🔄 Triggering generation from edit trip navigation');
            generationTriggeredFromEdit.current = true;
            generateItinerary();
            // Clear the state to prevent re-triggering on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state, trip, loading, generateItinerary]);

    const handleSuggestFlights = async () => {
        handleShowAiMessage(t('ai_in_action'));
        setIsAiWorking(true);
        setSuggestingFlights(true);
        setFlightSuggestionError('');
        setFlightSuggestions(null);

        try {
            const token = localStorage.getItem('token');
            const endpoint = `${API_BASE_URL}/flexible_flight_search.php`;

            const { start_location, end_location, start_date, flexible_duration_type, flexible_year, month_of_year, duration_days } = trip;

            if (!start_location || !end_location) {
                setFlightSuggestionError(t('missing_origin_dest'));
                setSuggestingFlights(false);
                setIsAiWorking(false);
                return;
            }

            const body = {
                origin: start_location,
                destination: end_location,
            };

            if (start_date) {
                body.date = start_date;
                body.duration = duration_days;
            } else if (flexible_duration_type && flexible_year && month_of_year) {
                body.month = month_of_year;
                body.year = flexible_year;
                body.durationType = flexible_duration_type;
            } else {
                setFlightSuggestionError(t('missing_date_flexible'));
                setSuggestingFlights(false);
                setIsAiWorking(false);
                return;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                setFlightSuggestions(data);
            } else {
                const errorData = await response.json();
                setFlightSuggestionError(errorData.message || t('flight_suggestions_failed'));
            }
        } catch (err) {
            console.error("Error getting flight suggestions:", err);
            setFlightSuggestionError(t('flight_suggestions_error'));
        } finally {
            setSuggestingFlights(false);
            setIsAiWorking(false);
        }
    };

    const handlePriceFlight = async (flight) => {
        // Assuming the first segment is the outbound flight
        const departureDate = flight.flightTimes[0].departureTime.split('T')[0];
        let returnDate = null;

        // If it's a round trip, find the return date from the last segment
        if (flight.flightTimes.length > 1) {
            returnDate = flight.flightTimes[flight.flightTimes.length - 1].arrivalTime.split('T')[0];
        }

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_PATH}/trips/update_dates.php` , {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    trip_id: tripId, 
                    start_date: departureDate,
                    end_date: returnDate || departureDate // If no return date, it's a one-way trip
                })
            });

            if (response.ok) {
                fetchTripData(); // Refetch trip data to update the UI
            } else {
                const errorData = await response.json();
                setError(errorData.message || t('update_dates_failed_generic'));
            }
        } catch (err) {
            console.error("Error updating trip dates:", err);
            setError(t('update_dates_error_generic'));
        }
    };

    const renderFlightInfo = (flight) => {
        if (!flight) {
            return <p>{t('no_flight_info')}</p>;
        }

        const formatISODuration = (duration) => {
            if (!duration) return '';
            const match = duration.match(/PT(\d+H)?(\d+M)?/);
            if (!match) return duration;
        
            const hours = match[1] ? parseInt(match[1].slice(0, -1)) : 0;
            const minutes = match[2] ? parseInt(match[2].slice(0, -1)) : 0;
        
            return `${hours}h ${minutes}m`;
        };
    
        const formatDateTime = (dateTimeString) => {
            const options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
            };
            return new Date(dateTimeString).toLocaleString(undefined, options);
        };

        const renderItinerary = (segments, layovers, carrierInfo, origin, destination) => {
            const stops = segments.length - 1;
            const airline = carrierInfo[0] ? `${carrierInfo[0].carrierName} (${carrierInfo[0].carrierCode})` : '';
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">
                        {origin} &rarr; {destination} ({stops} stop{stops !== 1 ? 's' : ''}) - {airline}
                    </Typography>
                    {segments.map((segment, index) => (
                        <div key={index}>
                            <Typography variant="body2">
                                {segments.length > 1 ? t('flight_segment_numbered_prefix', { number: index + 1 }) : t('flight_segment_prefix')}
                                {formatDateTime(segment.departureTime)} -&gt; {formatDateTime(segment.arrivalTime)} ({formatISODuration(segment.duration)})
                            </Typography>
                            {index < segments.length - 1 && layovers && layovers[index] &&
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                    {t('layover')}: {formatISODuration(layovers[index].duration)}
                                </Typography>
                            }
                        </div>
                    ))}
                </Box>
            );
        };

        try {
            const segments = flight.flightTimes || [];
            const midPoint = Math.floor(segments.length / 2);
            const outboundSegments = segments.slice(0, midPoint);
            const inboundSegments = segments.slice(midPoint);
            
            const layovers = flight.layoverTimes || [];
            const outboundLayovers = layovers.slice(0, outboundSegments.length - 1);
            const inboundLayovers = layovers.slice(outboundSegments.length - 1);

            const carrierInfo = flight.carrierInformation || [];
            const outboundCarrierInfo = carrierInfo.slice(0, outboundSegments.length);
            const inboundCarrierInfo = carrierInfo.slice(outboundSegments.length);

            return (
                <Paper elevation={1} sx={{ p: 2, mt: 1 }}>
                    {renderItinerary(outboundSegments, outboundLayovers, outboundCarrierInfo, flight.originAirportCode, flight.destinationAirportCode)}
                    {inboundSegments.length > 0 && renderItinerary(inboundSegments, inboundLayovers, inboundCarrierInfo, flight.destinationAirportCode, flight.originAirportCode)}

                    {flight.allowedLuggage && (
                        <Typography>
                            {t('luggage_carry_on')}{flight.allowedLuggage.carryOn ? t('yes') : t('no')}, {t('luggage_checked')}{flight.allowedLuggage.checkedBags}
                        </Typography>
                    )}
                    <Typography>{t('total_price')}{flight.totalPriceUSD}{t('currency_usd')}</Typography>
                    {flight.reason && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            <strong>{t('ai_recommendation')}</strong> {flight.reason}
                        </Typography>
                    )}
                    <Button onClick={() => handlePriceFlight(flight)} disabled={pricingFlight}>
                        {pricingFlight ? t('pricing_progress') : t('price_flight_btn')}
                    </Button>
                    {pricingError && <Alert severity="error">{pricingError}</Alert>}
                    {pricedFlightOffer && (
                        <Box mt={2}>
                            <Typography variant="h6">{t('priced_offer_title')}</Typography>
                            <pre>{JSON.stringify(pricedFlightOffer, null, 2)}</pre>
                        </Box>
                    )}
                </Paper>
            );
        } catch (e) {
            console.error("renderFlightInfo: Error during JSX rendering:", e, "Flight object:", flight);
            return <Alert severity="error">{t('render_flight_error')}</Alert>;
        }
    };

    const handlePinActivity = async (activityId, isPinned) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_PATH}/activities/pin.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ activity_id: activityId, is_pinned: isPinned ? 1 : 0 })
            });
            if (response.ok) {
                fetchTripData(); // Refresh data
            } else {
                const errorData = await response.json();
                setError(errorData.message || t('pin_status_failed'));
            }
        } catch (err) {
            console.error("Error pinning activity:", err);
            setError(t('pin_activity_error'));
        }
    };

    const handleDeleteActivity = async (activityId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_PATH}/activities.php?id=${activityId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchTripData(); // Refresh data
            } else {
                const errorData = await response.json();
                setError(errorData.message || t('delete_activity_failed'));
            }
        } catch (err) {
            console.error("Error deleting activity:", err);
            setError(t('delete_activity_error'));
        }
    };

    const handleLockDay = async (dayDate, isLocked) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_PATH}/days/lock.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ trip_id: tripId, day_date: dayDate, is_locked: isLocked ? 1 : 0 })
            });
            if (response.ok) {
                fetchTripData(); // Refresh data
            } else {
                const errorData = await response.json();
                setError(errorData.message || t('lock_day_failed'));
            }
        } catch (err) {
            console.error("Error locking day:", err);
            setError(t('lock_day_error'));
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!trip) {
        return <Alert severity="info">{t('no_trip_details')}</Alert>;
    }

    const getIconForTransportation = (transportation) => {
        const mode = typeof transportation === 'string' ? transportation : transportation?.transportation_mode;
        if (!mode) return null;

        switch (mode) {
            case 'flight':
                return <FlightTakeoffIcon />;
            case 'train':
                return <TrainIcon />;
            case 'bus':
                return <DirectionsBusIcon />;
            case 'boat':
                return <DirectionsBoatIcon />;
            case 'hotel':
                return <HotelIcon />;
            default:
                return null;
        }
    };

    const normalizeTransportationRecord = (record) => {
        if (!record) return null;
        const details = { ...(record.details || {}) };
        if (!details.airline && record.airline) details.airline = record.airline;
        if (!details.flightNumber && record.flight_number) details.flightNumber = record.flight_number;
        if (!details.freeText && record.free_text) details.freeText = record.free_text;
        if (!details.destination && record.destination_city) details.destination = record.destination_city;
        if (!details.destination && record.destination) details.destination = record.destination;
        if (!details.arrival && record.arrival) details.arrival = record.arrival;
        if (!details.to && record.to) details.to = record.to;
        if (!details.end && record.end) details.end = record.end;
        if (!details.location && record.location) details.location = record.location;

        const mode = record.transportation_mode || record.transport_type || '';
        return { transportation_mode: mode, details, raw: record };
    };

    const getTransportationLabel = (normalizedRecord) => {
        if (!normalizedRecord) return '';
        const { transportation_mode, details, raw } = normalizedRecord;
        if (transportation_mode === 'flight' || transportation_mode === 'Flight') {
            const flightLabel = `${details.airline ? details.airline : ''} ${details.flightNumber ? details.flightNumber : ''}`.trim();
            
            // Build route string including connecting flights
            let routeParts = [];
            if (raw?.departure_airport) routeParts.push(raw.departure_airport);
            if (raw?.arrival_airport) routeParts.push(raw.arrival_airport);
            
            // Add connecting flight destinations
            if (raw?.connecting_flights) {
                let connectingLegs = raw.connecting_flights;
                if (typeof connectingLegs === 'string') {
                    try {
                        connectingLegs = JSON.parse(connectingLegs);
                    } catch (e) {
                        connectingLegs = [];
                    }
                }
                if (Array.isArray(connectingLegs)) {
                    connectingLegs.forEach(leg => {
                        if (leg.arrivalAirport) routeParts.push(leg.arrivalAirport);
                    });
                }
            }
            
            // Also check return connecting flights for round trips
            if (raw?.return_connecting_flights) {
                let returnConnectingLegs = raw.return_connecting_flights;
                if (typeof returnConnectingLegs === 'string') {
                    try {
                        returnConnectingLegs = JSON.parse(returnConnectingLegs);
                    } catch (e) {
                        returnConnectingLegs = [];
                    }
                }
                if (Array.isArray(returnConnectingLegs) && returnConnectingLegs.length > 0) {
                    // Add return flight route separately
                    let returnRouteParts = [];
                    if (raw?.return_departure_airport) returnRouteParts.push(raw.return_departure_airport);
                    if (raw?.return_arrival_airport) returnRouteParts.push(raw.return_arrival_airport);
                    returnConnectingLegs.forEach(leg => {
                        if (leg.arrivalAirport) returnRouteParts.push(leg.arrivalAirport);
                    });
                }
            }
            
            const routeStr = routeParts.length > 1 ? ` (${routeParts.join(' → ')})` : '';
            if (flightLabel) return flightLabel + routeStr;
            if (routeStr) return routeStr.trim();
        }
        const fallbackParts = [
            details.freeText || details.free_text,
            details.destination,
            details.arrival,
            details.to,
            details.end,
            details.location,
            raw?.destination_city,
            raw?.destination,
            raw?.arrival,
            raw?.free_text
        ].filter(Boolean);
        return fallbackParts[0] || '';
    };

    const renderTransportationRecord = (record, index) => {
        const normalized = normalizeTransportationRecord(record);
        if (!normalized) return null;
        const icon = getIconForTransportation(normalized.transportation_mode);
        const label = getTransportationLabel(normalized);
        if (!icon && !label) return null;

        return (
            <Box key={`transport-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {icon}
                {label && <Typography sx={{ ml: 1 }}>{label}</Typography>}
            </Box>
        );
    };

    const renderTransportation = (transportation) => {
        if (!transportation) {
            return null;
        }

        const records = Array.isArray(transportation) ? transportation : [transportation];
        return records.map((record, index) => renderTransportationRecord(record, index));
    };

    // Not used anymore, replaced by direct ActivityItem usage below

    const renderLodging = (lodging) => {
        if (!lodging) return null;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HotelIcon />
                <Typography sx={{ ml: 1 }}>{lodging.name}</Typography>
            </Box>
        );
    };

    const renderFlightSection = ({ suggestions, isLoading, error, onSuggestClick, disabled }) => {
        if (isLoading) return <CircularProgress />;
        if (error) return <Alert severity="error">{error}</Alert>;
        if (!suggestions) {
            return (
                <Box sx={{ mt: 2 }}>
                    <Typography>{t('no_flight_info')}</Typography>
                    <Button
                        onClick={onSuggestClick}
                        disabled={isLoading}
                        sx={{ mt: 1 }}
                    >
                        {isLoading ? t('searching') : t('suggest_flights_btn')}
                    </Button>
                </Box>
            );
        }

        const suggestionMap = {
            'Cheapest': suggestions.cheapest,
            'Shortest': suggestions.shortest_direct,
            'Smart Stopover': suggestions.smart_stopover,
            "AI's Choice": suggestions.ai_choice,
        };

        const uniqueFlights = {};

        for (const title in suggestionMap) {
            const flight = suggestionMap[title];
            if (flight) {
                const flightKey = JSON.stringify(flight);
                if (uniqueFlights[flightKey]) {
                    uniqueFlights[flightKey].titles.push(title);
                } else {
                    uniqueFlights[flightKey] = {
                        flight,
                        titles: [title],
                    };
                }
            }
        }

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6">{t('flight_suggestions_title')}</Typography>
                {Object.values(uniqueFlights).map(({ flight, titles }, index) => (
                    <Box key={index} sx={{ mt: 2 }}>
                        <Typography variant="h6">{titles.join(' & ')}</Typography>
                        {renderFlightInfo(flight)}
                    </Box>
                ))}
                <Button
                    onClick={onSuggestClick}
                    disabled={isLoading}
                    sx={{ mt: 2 }}
                >
                    {isLoading ? t('searching') : t('refresh_suggestions_btn')}
                </Button>
            </Box>
        );
    };

    // Use computedTripDays (derived in fetchTripData) for rendering
    const tripDays = computedTripDays;

    // Map of activity_id -> connection info for connecting flights, built over
    // the whole trip in chronological (day, then start_time) order so a flight
    // connects to the previous flight even across a day boundary.
    const connectionByActivityId = {};
    {
        const ordered = (tripDays || []).flatMap(day => activitiesByDay[day.date] || []);
        let prevFlight = null;
        ordered.forEach(act => {
            if (!isFlightActivity(act)) return;
            if (prevFlight) {
                const connection = getFlightConnection(prevFlight, act);
                if (connection) connectionByActivityId[act.activity_id] = connection;
            }
            prevFlight = act;
        });
    }

    // Find the arrival flight (first flight activity, usually titled "Departure Flight")
    const arrivalFlightActivity = Object.values(activitiesByDay)
        .flat()
        .find(act => act.type === 'flight' && (act.title?.toLowerCase().includes('departure') || act.day === 1));

    const formatHotelDateTime = (value) => (value ? dayjs(value).format('ddd, MMM D h:mm A') : '--');

    // Handler for when interactive builder completes
    const handleInteractiveBuilderComplete = () => {
        setShowInteractiveBuilder(false);
        fetchTripData(); // Refresh data to show newly created activities
    };

    // Handler for when interactive builder is cancelled
    const handleInteractiveBuilderCancel = () => {
        setShowInteractiveBuilder(false);
    };
    
    // Handler for launching AI activity suggestions for a specific day
    const handleLaunchDaySuggestions = (day) => {
        // Find the day index in the route
        const dayIndex = trip?.route?.findIndex(r => r.day === day.dayNumber) ?? (day.dayNumber - 1);
        const routeDay = trip?.route?.[dayIndex] || {};
        
        setSuggestActivitiesForDay({
            dayNumber: day.dayNumber,
            dayIndex: dayIndex,
            city: routeDay.city || day.city || '',
            country: routeDay.country || day.country || '',
            date: day.date
        });
    };
    
    // Handler for when day-specific suggestions are completed
    const handleDaySuggestionsComplete = () => {
        setSuggestActivitiesForDay(null);
        fetchTripData(); // Refresh data to show newly created activities
    };
    
    // Handler for when day-specific suggestions are cancelled
    const handleDaySuggestionsCancel = () => {
        setSuggestActivitiesForDay(null);
    };

    // Handler to open AI chat for a specific day from InteractiveDailyActivityBuilder
    const handleOpenAiChatForDay = (dayNumber) => {
        console.log('handleOpenAiChatForDay called for day:', dayNumber);
        // Close the suggestions dialog first
        if (suggestActivitiesForDay) {
            setSuggestActivitiesForDay(null);
        }
        // Use setTimeout to ensure dialog closes before opening AI assistant
        // This prevents React batching from interfering with the state updates
        setTimeout(() => {
            console.log('Opening AI assistant for day:', dayNumber);
            setAiContext({ type: 'day', id: dayNumber, activity: null });
            setAiAssistantOpen(true);
        }, 100);
    };

    // If showing interactive builder, render it instead of main itinerary view
    if (showInteractiveBuilder && trip?.route && trip.route.length > 0) {
        return (
            <Box sx={{ 
                p: { xs: 1, sm: 2 }, 
                width: '100%',
                maxWidth: '100%', 
                overflow: 'hidden',
                boxSizing: 'border-box'
            }}>
                {/* Show "Set Trip Dates" button for flexible (no-date) trips */}
                {isTripFlexible && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarTodayIcon />}
                            onClick={handleOpenDatePicker}
                            size="small"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                        >
                            {t('set_trip_dates_btn')}
                        </Button>
                    </Box>
                )}
                <InteractiveDailyActivityBuilder
                    tripId={tripId}
                    tripData={trip}
                    onComplete={handleInteractiveBuilderComplete}
                    onCancel={handleInteractiveBuilderCancel}
                    setIsAiWorking={setIsAiWorking}
                    handleShowAiMessage={handleShowAiMessage}
                    onOpenAiChat={handleOpenAiChatForDay}
                />
                {/* Date picker dialog (needed here since this is an early return) */}
                <Dialog open={showDatePicker} onClose={handleCloseDatePicker} maxWidth="sm" fullWidth>
                    <DialogTitle>{t('set_trip_dates_dialog_title')}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                label={t('start_date_label')}
                                type="date"
                                fullWidth
                                value={tempStartDate}
                                onChange={(e) => setTempStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label={t('duration_days_label')}
                                type="number"
                                fullWidth
                                value={tempDuration}
                                onChange={(e) => setTempDuration(e.target.value)}
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDatePicker}>{t('cancel_btn')}</Button>
                        <Button onClick={handleSaveTripDates} variant="contained" color="primary">
                            {t('save_dates_btn')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            p: { xs: 1, sm: 2 }, 
            width: '100%',
            maxWidth: '100%', 
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {/* Page header - responsive */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: { xs: 1, sm: 2 },
                flexWrap: 'nowrap',
                gap: 0.5,
                width: '100%',
                overflow: 'hidden'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 }, minWidth: 0, flex: 1, overflow: 'hidden' }}>
                    <Typography 
                        variant="h4" 
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {tripTitle}
                    </Typography>
                    <IconButton 
                        onClick={() => setShowMap(!showMap)}
                        sx={{ color: 'primary.main', p: { xs: 0.5, sm: 1 } }}
                        size="small"
                    >
                        <MapIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                    </IconButton>
                    <IconButton 
                        onClick={() => setShowShareModal(true)}
                        sx={{ color: 'primary.main', p: { xs: 0.5, sm: 1 } }}
                        size="small"
                    >
                        <ShareIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                    </IconButton>
                    {linkedReports.length > 0 && (
                        <IconButton 
                            onClick={() => navigate(`/expenses/${linkedReports[0].id}`)}
                            sx={{ color: '#85bb65', p: { xs: 0.5, sm: 1 } }}
                            size="small"
                            title="Expenses"
                        >
                            <AttachMoneyIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                        </IconButton>
                    )}
                    <IconButton
                        component="a"
                        href={`${BASE_PATH}/trip/${tripId}/memories`}
                        sx={{ color: 'primary.main', p: { xs: 0.5, sm: 1 } }}
                        size="small"
                        title="Memories"
                    >
                        <CameraAltIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                    </IconButton>
                </Box>
                {isTripFlexible && (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<CalendarTodayIcon />}
                        onClick={handleOpenDatePicker}
                        size="small"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                    >
                        {t('set_trip_dates_btn')}
                    </Button>
                )}
            </Box>

            {/* Linked expense report banners */}
            {linkedReports.length > 0 && linkedReports.map(report => {
                const userCurrency = user?.currency || 'USD';
                let totalStr = '—';
                if (report.totals && report.totals.length > 0) {
                    if (expenseExchangeRates) {
                        const toRate = expenseExchangeRates[userCurrency] || 1;
                        const usdTotal = report.totals.reduce((sum, t) => {
                            const fromRate = expenseExchangeRates[t.currency] || 1;
                            return sum + parseFloat(t.total) / fromRate;
                        }, 0);
                        const converted = usdTotal * toRate;
                        totalStr = `${userCurrency} ${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    } else {
                        totalStr = report.totals.map(t => `${t.currency} ${parseFloat(t.total).toLocaleString(undefined, { maximumFractionDigits: 2 })}`).join(' + ');
                    }
                }
                const isExpanded = expandedReportId === report.id;
                return (
                    <Box key={report.id} sx={{
                        mb: 1, border: '1px solid', borderColor: 'primary.light',
                        borderRadius: 2, overflow: 'hidden',
                    }}>
                        {/* Collapsed header row */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                            bgcolor: 'primary.50', cursor: 'pointer',
                        }} onClick={() => setExpandedReportId(isExpanded ? null : report.id)}>
                            <AttachMoneyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>{report.title}</Typography>
                            <Typography variant="body2" color="primary" fontWeight={700}>{totalStr}</Typography>
                            <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/expenses/${report.id}`); }}>
                                <AttachMoneyIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                );
            })}

            {/* Extract cities from transportation activities and show map */}
            {activitiesByDay && (
                (() => {
                    // Collect cities in order of visit
                    const cities = [];
                    Object.values(activitiesByDay).forEach(dayActs => {
                        dayActs.forEach(act => {
                            if (act.type === 'transportation') {
                                const sd = act.structured_data || {};
                                if (act.subtype === 'flight' && sd.details) {
                                    // Amadeus format
                                    const details = sd.details;
                                    if (details.departure && details.departure.iataCode) cities.push(details.departure.iataCode);
                                    if (details.arrival && details.arrival.iataCode) cities.push(details.arrival.iataCode);
                                } else if (sd.transportation_details) {
                                    // Legacy structured_data table format
                                    const td = sd.transportation_details;
                                    if (td.from) cities.push(td.from);
                                    if (td.to) cities.push(td.to);
                                } else {
                                    // Email-import format: origin_city / destination_city
                                    const from = sd.origin_city || act.location || '';
                                    const to = sd.destination_city || act.destination || '';
                                    if (from) cities.push(from);
                                    if (to) cities.push(to);
                                }
                            } else if (act.location) {
                                // Non-transportation: use location field
                                cities.push(act.location);
                            }
                        });
                    });
                    // Remove consecutive duplicates, preserve order
                    const simplifiedCities = cities.filter((city, idx, arr) => idx === 0 || city !== arr[idx - 1]);
                    // Don't show map while AI is working to avoid refreshing/presenting it
                    // Only show map when user clicks the map icon (showMap is true)
                    return (!isAiWorking && showMap && simplifiedCities.length > 0) ? (
                        <TripMap cities={simplifiedCities} apiKey={GOOGLE_MAPS_API_KEY} />
                    ) : null;
                })()
            )}

            <AddActivityModal open={showAddActivityModal} handleClose={handleCloseAddActivityModal} tripId={tripId} trip={trip} tripDays={tripDays} selectedDay={selectedDay} showLoggingOption={true} />
            {/* Edit modal, pass activity and isEdit flag */}
            <AddActivityModal open={showEditActivityModal} handleClose={handleCloseEditActivityModal} tripId={tripId} trip={trip} tripDays={tripDays} activity={editActivity} isEdit={true} />
            <TransportationFormModal
                open={showTransportationModal}
                onClose={handleCloseTransportationModal}
                tripId={tripId}
                dayLabel={selectedDayForTransport}
                defaultDestination={transportDefaultDestination}
                defaultDepartureDate={transportDefaultDate}
                tripDays={tripDays}
                selectedDayNumber={selectedDay}
                existingRecord={editingTransportationRecord}
            />

            <Dialog open={transportationDetailsOpen && !!selectedTransportationRecord} onClose={handleCloseTransportationDetails} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FlightTakeoffIcon sx={{ color: '#3b82f6' }} />
                        <Typography variant="h6" component="span">Flight Details</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditTransportationRecord(selectedTransportationRecord)} sx={{ color: '#3b82f6' }}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteTransportationRecord(selectedTransportationRecord)} sx={{ color: '#ef4444' }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={handleCloseTransportationDetails}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedTransportationRecord && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {(selectedTransportationRecord.airline || selectedTransportationRecord.flight_number) && (
                                <Typography variant="body2"><strong>Flight:</strong> {[selectedTransportationRecord.airline, selectedTransportationRecord.flight_number].filter(Boolean).join(' ')}</Typography>
                            )}
                            {selectedTransportationRecord.flight_confirmation && (
                                <Typography variant="body2"><strong>Confirmation:</strong> {selectedTransportationRecord.flight_confirmation}</Typography>
                            )}
                            {selectedTransportationRecord.flight_type && (
                                <Typography variant="body2"><strong>Type:</strong> {selectedTransportationRecord.flight_type}</Typography>
                            )}
                            {(selectedTransportationRecord.departure_airport || selectedTransportationRecord.arrival_airport) && (
                                <Typography variant="body2"><strong>Route:</strong> {[selectedTransportationRecord.departure_airport, selectedTransportationRecord.arrival_airport].filter(Boolean).join(' → ')}</Typography>
                            )}
                            {(selectedTransportationRecord.departure_time || selectedTransportationRecord.arrival_time) && (
                                <Typography variant="body2"><strong>Times:</strong> {[selectedTransportationRecord.departure_time, selectedTransportationRecord.arrival_time].filter(Boolean).join(' - ')}</Typography>
                            )}
                            {selectedTransportationRecord.return_flight_number && <Divider sx={{ my: 0.5 }} />}
                            {selectedTransportationRecord.return_flight_number && (
                                <Typography variant="body2"><strong>Return Flight:</strong> {[selectedTransportationRecord.return_airline, selectedTransportationRecord.return_flight_number].filter(Boolean).join(' ')}</Typography>
                            )}
                            {(selectedTransportationRecord.return_departure_airport || selectedTransportationRecord.return_arrival_airport) && (
                                <Typography variant="body2"><strong>Return Route:</strong> {[selectedTransportationRecord.return_departure_airport, selectedTransportationRecord.return_arrival_airport].filter(Boolean).join(' → ')}</Typography>
                            )}
                            {(selectedTransportationRecord.return_departure_time || selectedTransportationRecord.return_arrival_time) && (
                                <Typography variant="body2"><strong>Return Times:</strong> {[selectedTransportationRecord.return_departure_time, selectedTransportationRecord.return_arrival_time].filter(Boolean).join(' - ')}</Typography>
                            )}
                            {selectedTransportationRecord.free_text && (
                                <Typography variant="body2"><strong>Notes:</strong> {selectedTransportationRecord.free_text}</Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTransportationDetails} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
            
            {/* Activity Details Modal with Image Gallery */}
            <ActivityDetailsModal
                open={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                activity={selectedActivity}
            />
            
            {/* Share Trip Modal */}
            <ShareTripModal
                open={showShareModal}
                onClose={() => setShowShareModal(false)}
                tripId={tripId}
                tripName={tripTitle}
                user={user}
            />
            
            <HotelReservationDialog
                open={hotelDialogOpen}
                onClose={handleCloseHotelDialog}
                dayLabel={selectedHotelDay?.label}
                dayDate={selectedHotelDay?.date}
                initialValues={selectedHotelStay || undefined}
                dayLocationHint={selectedHotelDay?.locationHint}
                onSave={handleSaveHotelReservation}
                onDelete={handleDeleteHotelStay}
            />

            <Box sx={{ p: { xs: 0, sm: 2 }, mb: 2, width: '100%' }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                    {/* Trip Duration Explanation - Display why this number of days */}
                    {trip?.duration_explanation && (
                        <Box sx={{
                            mb: 2,
                            p: { xs: 1.2, sm: 1.5 },
                            borderRadius: 2,
                            bgcolor: '#fef3c7',
                            border: '1px solid #fcd34d',
                            cursor: 'default'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#92400e', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                    <strong>Why {trip.duration_days} days for this trip:</strong> {trip.duration_explanation}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    
                    {tripDays.map((day, index) => {
                        const dayTransportationRecords = dailyTransportation[day.date];
                        const hasDayTransportation = Array.isArray(dayTransportationRecords) && dayTransportationRecords.length > 0;
                        const dayEvents = staysByDate[day.date] || [];
                        const hasHotelReservation = dayEvents.length > 0;
                        const hasStayOrCheckin = dayEvents.some(evt => evt.type !== 'checkout');
                        const isCheckoutOnly = hasHotelReservation && !hasStayOrCheckin;
                        const uniqueStaysForDay = [];
                        const seenStayIds = new Set();
                        dayEvents.forEach((event) => {
                            if (!seenStayIds.has(event.stayId)) {
                                seenStayIds.add(event.stayId);
                                uniqueStaysForDay.push(event.stay);
                            }
                        });
                        const staysForIcons = uniqueStaysForDay.slice(0, 2);
                        const extraStayIcons = Math.max(0, uniqueStaysForDay.length - staysForIcons.length);
                        
                        // Get location hint for the day to display city name
                        // Prefer the route city for this day number over the trip start/end airport
                        const routeDayData = trip?.route?.find(r => r.day === day.dayNumber) || {};
                        const fallbackLocation = routeDayData.city
                            ? `${routeDayData.city}${routeDayData.country ? ', ' + routeDayData.country : ''}`
                            : trip?.end_location || trip?.start_location || '';
                        const dayLocationHint = getDayLocationHint(day, activitiesByDay, fallbackLocation, dayTransportationRecords, tripDays);
                        
                        const isPastDay = day.date && !day.date.startsWith('day-') && dayjs(day.date).isBefore(dayjs(), 'day');

                        // Debug logging
                        if (index === 0) {
                            console.log('🏙️ Day location hint for Day 1:', {
                                dayLocationHint,
                                activitiesCount: (activitiesByDay?.[day.date] || []).length,
                                firstActivityLocation: (activitiesByDay?.[day.date] || [])[0]?.location,
                                fallbackLocation
                            });
                        }
                        
                        return (
                            <Box key={day.date} sx={{ width: '100%' }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 1, sm: 2 },
                                        minHeight: { xs: 'auto', sm: '100px' },
                                        border: '1px solid',
                                        borderColor: isPastDay ? '#e0e0e0' : hasStayOrCheckin ? 'success.light' : isCheckoutOnly ? 'warning.light' : '#e5e7eb',
                                        borderRadius: 2,
                                        bgcolor: isPastDay ? '#f5f5f5' : 'white',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Day Header - Responsive */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        mb: 1.5,
                                        pb: 1,
                                        borderBottom: '1px solid #f0f0f0',
                                        flexWrap: 'nowrap',
                                        gap: 1
                                    }}>
                                        {/* Day title with optional date - compact on mobile */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0, flexShrink: 1 }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: 600, 
                                                    color: isPastDay ? 'text.disabled' : 'text.primary',
                                                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {t('day_label')} {day.dayNumber}
                                            </Typography>
                                            {day.date && !day.date.startsWith('day-') && (
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: 'text.secondary', 
                                                        fontSize: { xs: '0.7rem', sm: '0.85rem' }
                                                    }}
                                                >
                                                    {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                                </Typography>
                                            )}
                                            {/* Display city name if available */}
                                            {dayLocationHint?.city && (
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: 'primary.main',
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                                    }}
                                                >
                                                    {dayLocationHint.city}
                                                </Typography>
                                            )}
                                            {/* Desktop chips */}
                                            {hasStayOrCheckin && (
                                                <Chip 
                                                    icon={<HotelIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={t('hotel_booked') || 'Hotel'}
                                                    size="small"
                                                    sx={{ 
                                                        display: { xs: 'none', sm: 'flex' },
                                                        bgcolor: '#e8f5e9', 
                                                        color: '#2e7d32', 
                                                        height: '22px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        '& .MuiChip-icon': { color: '#2e7d32' }
                                                    }}
                                                    onClick={() => handleOpenHotelDialog(day, uniqueStaysForDay[0]?.id)}
                                                />
                                            )}
                                            {hasDayTransportation && (
                                                <Chip 
                                                    icon={<FlightTakeoffIcon sx={{ fontSize: '14px !important' }} />}
                                                    label={t('transport') || 'Travel'}
                                                    size="small"
                                                    sx={{ 
                                                        display: { xs: 'none', sm: 'flex' },
                                                        bgcolor: '#e3f2fd', 
                                                        color: '#1565c0', 
                                                        height: '22px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        '& .MuiChip-icon': { color: '#1565c0' }
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        
                                        {/* Action buttons - visible on all screen sizes with 20px spacing */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
                                            <Tooltip title={t('day_mgmt_view_day') || 'View Day'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/trip/${tripId}/day/${day.date}`)}
                                                    sx={{ color: 'success.main', p: { xs: 0.5, sm: 0.75 } }}
                                                >
                                                    <TodayIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setAiContext({ type: 'day', id: day.dayNumber, activity: null });
                                                    setAiAssistantOpen(true);
                                                }}
                                                sx={{ color: 'primary.main', p: { xs: 0.5, sm: 0.75 } }}
                                            >
                                                <AIIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                            </IconButton>
                                            <Tooltip title={t('suggest_activities') || 'AI Activity Suggestions'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleLaunchDaySuggestions(day)}
                                                    sx={{ 
                                                        p: { xs: 0.5, sm: 0.75 },
                                                        '& svg': { fontSize: { xs: '18px', sm: '20px' } }
                                                    }}
                                                    className="gemini-icon-button"
                                                >
                                                    <WandIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {!isTripFlexible && !hasStayOrCheckin && index !== tripDays.length - 1 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        // Get the location from last activity of the day
                                                        const transportationRecords = dailyTransportation[day.date] || [];
                                                        const fallbackLocation = trip?.end_location || trip?.start_location || '';
                                                        const locationData = getDayLocationHint(day, activitiesByDay, fallbackLocation, transportationRecords, computedTripDays);
                                                        
                                                        // Calculate check-out date (next day)
                                                        const checkOutDate = new Date(day.date);
                                                        checkOutDate.setDate(checkOutDate.getDate() + 1);
                                                        
                                                        // Generate Expedia hotel search link with location data
                                                        const hotelLink = generateExpediaHotelSearchLink(
                                                            locationData,
                                                            day.date,
                                                            checkOutDate.toISOString().split('T')[0]
                                                        );
                                                        
                                                        if (hotelLink) {
                                                            window.open(hotelLink, '_blank');
                                                        }
                                                    }}
                                                    sx={{ color: 'text.secondary', p: { xs: 0.5, sm: 0.75 } }}
                                                >
                                                    <HotelIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                                </IconButton>
                                            )}
                                            <IconButton 
                                                size="small"
                                                onClick={(e) => handleAddActivityClick(e, day.dayNumber)}
                                                sx={{ color: 'primary.main', p: { xs: 0.5, sm: 0.75 } }}
                                            >
                                                <AddIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                            </IconButton>
                                            <Menu
                                                anchorEl={activityMenuAnchorEl}
                                                open={Boolean(activityMenuAnchorEl) && selectedDay === day.dayNumber}
                                                onClose={handleAddActivityMenuClose}
                                            >
                                                <MenuItem onClick={() => handleAddActivityType('activity')}>
                                                    {t('add_activity') || 'Add Activity'}
                                                </MenuItem>
                                                <MenuItem onClick={() => handleAddActivityType('transportation')}>
                                                    {t('add_transportation') || 'Add Transportation'}
                                                </MenuItem>
                                                <MenuItem onClick={() => handleAddActivityType('lodging')}>
                                                    {t('add_lodging') || 'Add Lodging'}
                                                </MenuItem>
                                            </Menu>
                                            <IconButton 
                                                size="small"
                                                onClick={() => handleLockDay(day.date, !lockedDays[day.date])}
                                                sx={{ color: lockedDays[day.date] ? 'success.main' : 'text.secondary', p: { xs: 0.5, sm: 0.75 }, display: { xs: 'none', sm: 'flex' } }}
                                            >
                                                {lockedDays[day.date] ? <LockIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} /> : <LockOpenIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    
                                    {/* Day content: greyed out for past days */}
                                    <Box sx={{ opacity: isPastDay ? 0.45 : 1, transition: 'opacity 0.2s' }}>

                                    {/* Hotel stays - Simplified inline display */}
                                    {uniqueStaysForDay.length > 0 && (
                                        <Box sx={{ mb: 1.5, width: '100%', overflow: 'hidden' }}>
                                            {uniqueStaysForDay.map((stay) => (
                                                <Box
                                                    key={stay.id}
                                                    onClick={() => handleOpenHotelDialog(day, stay.id)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: { xs: 1, sm: 1.5 },
                                                        p: { xs: 1, sm: 1.25 },
                                                        mb: 0.75,
                                                        borderRadius: 2,
                                                        bgcolor: '#f0fdf4',
                                                        border: '1px solid #bbf7d0',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                        overflow: 'hidden',
                                                        '&:hover': { bgcolor: '#dcfce7', borderColor: '#86efac' }
                                                    }}
                                                >
                                                    <HotelIcon sx={{ color: '#22c55e', fontSize: { xs: '18px', sm: '20px' }, flexShrink: 0 }} />
                                                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                color: '#166534',
                                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {stay.hotelName || t('hotel_reservation_title')}
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                color: '#15803d',
                                                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                                display: 'block',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {stay.checkInDateTime && stay.checkOutDateTime && `${dayjs(stay.checkInDateTime).format('MMM D')} - ${dayjs(stay.checkOutDateTime).format('MMM D')}`}
                                                        </Typography>
                                                    </Box>
                                                    {stay.reference && (
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                display: { xs: 'none', sm: 'block' },
                                                                color: '#16a34a', 
                                                                bgcolor: '#dcfce7', 
                                                                px: 1, 
                                                                py: 0.25, 
                                                                borderRadius: 1,
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            #{stay.reference}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Transportation records from transportation table */}
                                    {hasDayTransportation && (
                                        <Box sx={{ mb: 1.5, width: '100%', overflow: 'hidden' }}>
                                            {dayTransportationRecords.map((record, idx) => {
                                                const normalized = normalizeTransportationRecord(record);
                                                const label = getTransportationLabel(normalized);
                                                const timeLabel = [record?.departure_time, record?.arrival_time].filter(Boolean).join(' • ');
                                                const reference = record?.flight_confirmation || record?.booking_reference || '';
                                                const mode = (normalized?.transportation_mode || record?.transport_type || '').toLowerCase();
                                                const descriptor = `${label || ''} ${record?.free_text || ''} ${record?.destination_city || ''}`.toLowerCase();

                                                let transportIcon = <FlightTakeoffIcon sx={{ color: '#3b82f6', fontSize: { xs: '18px', sm: '20px' }, flexShrink: 0 }} />;
                                                if (mode !== 'flight') {
                                                    if (/train|rail/.test(descriptor)) {
                                                        transportIcon = <TrainIcon sx={{ color: '#3b82f6', fontSize: { xs: '18px', sm: '20px' }, flexShrink: 0 }} />;
                                                    } else if (/bus|coach/.test(descriptor)) {
                                                        transportIcon = <DirectionsBusIcon sx={{ color: '#3b82f6', fontSize: { xs: '18px', sm: '20px' }, flexShrink: 0 }} />;
                                                    } else if (/boat|ferry|ship/.test(descriptor)) {
                                                        transportIcon = <DirectionsBoatIcon sx={{ color: '#3b82f6', fontSize: { xs: '18px', sm: '20px' }, flexShrink: 0 }} />;
                                                    } else {
                                                        transportIcon = <TrainIcon sx={{ color: '#3b82f6', fontSize: { xs: '18px', sm: '20px' }, flexShrink: 0 }} />;
                                                    }
                                                }

                                                return (
                                                    <Box
                                                        key={`transpcard-${record?.id || idx}`}
                                                        onClick={() => handleOpenTransportationDetails(record)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: { xs: 1, sm: 1.5 },
                                                            p: { xs: 1, sm: 1.25 },
                                                            mb: 0.75,
                                                            borderRadius: 2,
                                                            bgcolor: '#eff6ff',
                                                            border: '1px solid #bfdbfe',
                                                            overflow: 'hidden',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s',
                                                            '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' }
                                                        }}
                                                    >
                                                        {transportIcon}
                                                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: '#1e3a8a',
                                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {label || t('transport') || 'Transportation'}
                                                            </Typography>
                                                            {timeLabel && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: '#1d4ed8',
                                                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                                        display: 'block',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {timeLabel}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {reference && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: { xs: 'none', sm: 'block' },
                                                                    color: '#1d4ed8',
                                                                    bgcolor: '#dbeafe',
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: 1,
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                #{reference}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                    
                                    {/* City Context - Display explanation for the location */}
                                    {(() => {
                                        const dayActivities = activitiesByDay[day.date] || [];
                                        const firstNonFlightActivity = dayActivities.find(act => act.type !== 'flight' && act.type !== 'transportation');
                                        const locationKey = firstNonFlightActivity?.location;
                                        const context = locationKey ? cityContexts[locationKey] : null;
                                        
                                        if (!context) return null;
                                        
                                        const isExpanded = expandedCityContexts[locationKey];
                                        
                                        return (
                                            <Box
                                                sx={{
                                                    mb: 1.5,
                                                    p: { xs: 1.2, sm: 1.5 },
                                                    borderRadius: 2,
                                                    bgcolor: '#eff6ff',
                                                    border: '1px solid #bfdbfe',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        bgcolor: '#dbeafe',
                                                        borderColor: '#7dd3fc'
                                                    }
                                                }}
                                                onClick={() => setExpandedCityContexts(prev => ({ ...prev, [locationKey]: !prev[locationKey] }))}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <InfoOutlinedIcon sx={{ color: '#0369a1', fontSize: '20px', flexShrink: 0, mt: 0.3 }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                color: '#0369a1',
                                                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            {locationKey}
                                                        </Typography>
                                                        
                                                        {isExpanded && (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                {context.why_selected && (
                                                                    <Box>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                fontWeight: 600, 
                                                                                color: '#064e3b',
                                                                                display: 'block',
                                                                                fontSize: '0.75rem',
                                                                                mb: 0.3
                                                                            }}
                                                                        >
                                                                            Why Selected
                                                                        </Typography>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                color: '#0c5460',
                                                                                display: 'block',
                                                                                fontSize: '0.8rem',
                                                                                lineHeight: 1.5
                                                                            }}
                                                                        >
                                                                            {context.why_selected}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                
                                                                {context.city_nature && (
                                                                    <Box>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                fontWeight: 600, 
                                                                                color: '#064e3b',
                                                                                display: 'block',
                                                                                fontSize: '0.75rem',
                                                                                mb: 0.3
                                                                            }}
                                                                        >
                                                                            City Nature
                                                                        </Typography>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                color: '#0c5460',
                                                                                display: 'block',
                                                                                fontSize: '0.8rem',
                                                                                lineHeight: 1.5
                                                                            }}
                                                                        >
                                                                            {context.city_nature}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                
                                                                {context.what_to_expect && (
                                                                    <Box>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                fontWeight: 600, 
                                                                                color: '#064e3b',
                                                                                display: 'block',
                                                                                fontSize: '0.75rem',
                                                                                mb: 0.3
                                                                            }}
                                                                        >
                                                                            What to Expect
                                                                        </Typography>
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                color: '#0c5460',
                                                                                display: 'block',
                                                                                fontSize: '0.8rem',
                                                                                lineHeight: 1.5
                                                                            }}
                                                                        >
                                                                            {context.what_to_expect}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}
                                                        
                                                        {!isExpanded && (
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    color: '#0284c7',
                                                                    fontSize: '0.75rem',
                                                                    fontStyle: 'italic'
                                                                }}
                                                            >
                                                                Click to read city insights...
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })()}
                                    
                                    {/* Activities list */}
                                    <Droppable droppableId={`day-${day.dayNumber}`}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                style={{
                                                    minHeight: '20px',
                                                    backgroundColor: snapshot.isDraggingOver ? '#f0f9ff' : 'transparent',
                                                    borderRadius: '8px',
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                            >
                                                {(() => {
                                                    const dayActivities = activitiesByDay[day.date] || [];
                                                    return dayActivities.map((activity, idx) => {
                                                        const nextNonLodging = dayActivities.slice(idx + 1).find(act => act.type !== 'lodging');
                                                        const previousNonLodging = [...dayActivities.slice(0, idx)].reverse().find(act => act.type !== 'lodging') || null;
                                                        // Detect a connecting flight: the previous flight in the
                                                        // trip (possibly on an earlier day) arrives at this
                                                        // flight's departure airport, within 12h / same day.
                                                        // Precomputed trip-wide so cross-day layovers are caught.
                                                        const connection = connectionByActivityId[activity.activity_id] || null;
                                                        return (
                                                            <React.Fragment key={`activity-${activity.activity_id}`}>
                                                            {connection && (
                                                                <Box sx={{
                                                                    display: 'flex', alignItems: 'center', gap: 1,
                                                                    mx: { xs: 1, sm: 3 }, my: 0.5, px: 1.5, py: 0.75,
                                                                    borderLeft: '3px dashed', borderColor: '#3b82f6',
                                                                    bgcolor: 'rgba(59,130,246,0.06)', borderRadius: 1,
                                                                }}>
                                                                    <ConnectingAirportsIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                                                                    <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 600 }}>
                                                                        {t('flight_connection', 'Connection')}
                                                                        {connection.city ? ` — ${connection.city}` : ''}
                                                                        {connection.code ? ` (${connection.code})` : ''}
                                                                    </Typography>
                                                                    {connection.layoverText && (
                                                                        <Typography variant="caption" sx={{ color: '#64748b', ml: 0.5 }}>
                                                                            · {t('layover', 'Layover')} {connection.layoverText}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                            <Draggable
                                                                draggableId={`activity-${activity.activity_id}`}
                                                                index={idx}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                    >
                                                                        <ActivityItem
                                                                            activity={activity}
                                                                            dayLabel={day.label}
                                                                            dayDate={day.date}
                                                                            onDelete={handleDeleteActivity}
                                                                            nextActivity={nextNonLodging}
                                                                            previousActivity={previousNonLodging}
                                                                            onEdit={handleEditActivity}
                                                                            onFlightUpdate={handleFlightUpdateClick}
                                                                            onFlightUpdateDisabled={handleFlightUpdateDisabled}
                                                                            isTripFlexible={isTripFlexible}
                                                                            arrivalFlightActivity={arrivalFlightActivity}
                                                                            onAskAI={(activity) => {
                                                                                setAiContext({ type: 'activity', id: activity.activity_id, activity: activity });
                                                                                setAiAssistantOpen(true);
                                                                            }}
                                                                            onShowDetails={(activity) => {
                                                                                setSelectedActivity(activity);
                                                                                setShowDetailsModal(true);
                                                                            }}
                                                                            onOpenTransportation={handleOpenTransportationModal}
                                                                            hasTransportationInfo={hasDayTransportation}
                                                                            isDragging={snapshot.isDragging}
                                                                            dragHandleProps={provided.dragHandleProps}
                                                                            onPin={handlePinActivity}
                                                                            trip={trip}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })()}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>

                                    </Box> {/* end day content opacity wrapper */}
                                </Paper>
                            </Box>
                        );
                    })}
                </Box>
                </DragDropContext>
            </Box>

            {/* Snackbar for flexible trip message */}
            <Snackbar
                open={showFlexibleTripMessage}
                autoHideDuration={2000}
                onClose={() => setShowFlexibleTripMessage(false)}
                message={t('flexible_trip_update_error')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            {/* Snackbar: no expense report assigned to this trip */}
            <Snackbar
                open={noExpenseSnack}
                autoHideDuration={4000}
                onClose={() => setNoExpenseSnack(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNoExpenseSnack(false)} severity="info" sx={{ width: '100%' }}>
                    No expense report is assigned to this trip. Go to&nbsp;
                    <strong>My Expenses</strong>&nbsp;to create or assign one.
                </Alert>
            </Snackbar>

            {/* Dialog for AI Activity Suggestions for a specific day */}
            <Dialog 
                open={!!suggestActivitiesForDay} 
                onClose={handleDaySuggestionsCancel} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: { 
                        maxHeight: '90vh',
                        m: { xs: 1, sm: 2 }
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 1
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" component="span">
                            {t('day_label')} {suggestActivitiesForDay?.dayNumber}
                        </Typography>
                        {suggestActivitiesForDay?.city && (
                            <Chip 
                                label={suggestActivitiesForDay.city} 
                                size="small"
                            />
                        )}
                    </Box>
                    <IconButton
                        onClick={handleDaySuggestionsCancel}
                        sx={{ 
                            color: 'error.main',
                            '&:hover': {
                                bgcolor: 'error.light',
                                color: 'error.contrastText'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {suggestActivitiesForDay && trip?.route && (
                        <InteractiveDailyActivityBuilder
                            tripId={tripId}
                            tripData={trip}
                            onComplete={handleDaySuggestionsComplete}
                            onCancel={handleDaySuggestionsCancel}
                            setIsAiWorking={setIsAiWorking}
                            handleShowAiMessage={handleShowAiMessage}
                            initialDayIndex={suggestActivitiesForDay.dayIndex}
                            singleDayMode={true}
                            onOpenAiChat={handleOpenAiChatForDay}
                            existingActivities={activitiesByDay}
                            autoLoad={true}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog for setting trip dates */}
            <Dialog open={showDatePicker} onClose={handleCloseDatePicker} maxWidth="sm" fullWidth>
                <DialogTitle>{t('set_trip_dates_dialog_title')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            label={t('start_date_label')}
                            type="date"
                            fullWidth
                            value={tempStartDate}
                            onChange={(e) => setTempStartDate(e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label={t('duration_days_label')}
                            type="number"
                            fullWidth
                            value={tempDuration}
                            onChange={(e) => setTempDuration(e.target.value)}
                            inputProps={{ min: 1 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDatePicker}>{t('cancel_btn')}</Button>
                    <Button onClick={handleSaveTripDates} variant="contained" color="primary">
                        {t('save_dates_btn')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Floating AI Assistant Button */}
            <Fab
                color="primary"
                aria-label="ai assistant"
                onClick={() => {
                    setAiContext({ type: 'general', id: null, activity: null });
                    setAiAssistantOpen(true);
                }}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000
                }}
            >
                <AIIcon />
            </Fab>

            {/* AI Assistant Drawer */}
            <AIAssistant
                open={aiAssistantOpen}
                onClose={() => setAiAssistantOpen(false)}
                tripId={tripId}
                tripData={trip}
                contextType={aiContext.type}
                contextId={aiContext.id}
                contextActivity={aiContext.activity}
                onOpenAddFlightModal={handleAIOpenAddFlightModal}
                onOpenAddActivityModal={handleAIOpenAddActivityModal}
                onOpenEditActivityModal={handleAIOpenEditActivityModal}
            />
        </Box>
    );
};

const HotelReservationDialog = ({
    open,
    onClose,
    dayLabel,
    dayDate,
    initialValues = {},
    dayLocationHint = {},
    onSave,
    onDelete
}) => {
    const { t } = useTranslation();
    const [checkInDate, setCheckInDate] = useState('');
    const [checkOutDate, setCheckOutDate] = useState('');
    const [checkInTime, setCheckInTime] = useState('15:00');
    const [checkOutTime, setCheckOutTime] = useState('11:00');
    const [notes, setNotes] = useState('');
    const [reference, setReference] = useState('');
    const [hotelName, setHotelName] = useState('');
    const [hotelAddress, setHotelAddress] = useState('');
    const [hotelCity, setHotelCity] = useState('');
    const [hotelCountry, setHotelCountry] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [suggestionError, setSuggestionError] = useState('');
    const [selectedSuggestionId, setSelectedSuggestionId] = useState(null);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const hintCity = dayLocationHint?.city || '';
    const hintCountry = dayLocationHint?.country || '';
    const hintLocationLat = dayLocationHint?.locationLat;
    const hintLocationLng = dayLocationHint?.locationLng;
    const hintLocationName = dayLocationHint?.locationName || '';
    
    // Log the hint as soon as component renders
    console.warn('HotelReservationDialog: component rendered with hint', {
        dayLocationHint,
        hintCity,
        hintCountry,
        hintLocationName,
        open
    });

    const hasInitializedValues = useRef(false);
    useEffect(() => {
        if (!open) {
            hasInitializedValues.current = false;
            return;
        }
        if (hasInitializedValues.current) return;
        const incomingCheckIn = initialValues?.checkInDateTime ? dayjs(initialValues.checkInDateTime) : null;
        const incomingCheckOut = initialValues?.checkOutDateTime ? dayjs(initialValues.checkOutDateTime) : null;
        const fallbackCheckIn = dayDate || '';
        const fallbackCheckOut = dayDate ? dayjs(dayDate).add(1, 'day').format('YYYY-MM-DD') : '';
        setCheckInDate(incomingCheckIn ? incomingCheckIn.format('YYYY-MM-DD') : fallbackCheckIn);
        setCheckOutDate(incomingCheckOut ? incomingCheckOut.format('YYYY-MM-DD') : fallbackCheckOut);
        setCheckInTime(incomingCheckIn ? incomingCheckIn.format('HH:mm') : '15:00');
        setCheckOutTime(incomingCheckOut ? incomingCheckOut.format('HH:mm') : '11:00');
        setNotes(initialValues?.notes || '');
        setReference(initialValues?.reference || '');
        setHotelName(initialValues?.hotelName || '');
        setHotelAddress(initialValues?.hotelAddress || '');
        setHotelCity(initialValues?.hotelCity || hintCity);
        setHotelCountry(initialValues?.hotelCountry || hintCountry);
        setSelectedSuggestion(null);
        setSelectedSuggestionId(initialValues?.hotelMapPlaceId || null);
        console.debug('HotelReservationDialog opened', {
            dayLabel,
            dayDate,
            incomingCheckIn: incomingCheckIn?.format(),
            incomingCheckOut: incomingCheckOut?.format(),
            initialValues,
            hintCity,
            hintCountry
        });
        // If there's existing data, start in view mode; otherwise start in edit mode
        setIsEditMode(!initialValues?.id);
        hasInitializedValues.current = true;
    }, [open, initialValues, dayDate, hintCity, hintCountry]);

    useEffect(() => {
        if (!open) {
            setSuggestions([]);
            setSuggestionError('');
            setLoadingSuggestions(false);
            setSelectedSuggestion(null);
            setSelectedSuggestionId(null);
            setIsEditMode(false);
        }
    }, [open]);

    const handleRangeSelect = (start, end) => {
        console.debug('HotelReservationDialog: stay dates selected', { start, end });
        setCheckInDate(start);
        setCheckOutDate(end);
    };

    const handleCheckInTimeChange = (value) => {
        console.debug('HotelReservationDialog: check-in time change', value);
        setCheckInTime(value);
    };
    const handleCheckOutTimeChange = (value) => {
        console.debug('HotelReservationDialog: check-out time change', value);
        setCheckOutTime(value);
    };
    const handleAddressChange = (value) => {
        console.debug('HotelReservationDialog: address change', value);
        setHotelAddress(value);
        setSelectedSuggestion(null);
        setSelectedSuggestionId(null);
        setSuggestionError('');
        setSuggestions([]);
    };
    const handleReferenceChange = (value) => {
        console.debug('HotelReservationDialog: booking reference change', value);
        setReference(value);
    };
    const handleNotesChange = (value) => {
        console.debug('HotelReservationDialog: notes change', value);
        setNotes(value);
    };

    const isValidRange = checkInDate && checkOutDate && !dayjs(checkOutDate).isBefore(dayjs(checkInDate));

    const fetchHotelSuggestions = useCallback(async (query) => {
        const trimmed = query?.trim();
        if (!trimmed) {
            setSuggestions([]);
            setSuggestionError('');
            return;
        }
        console.info('HotelReservationDialog: fetching hotel suggestions', { query: trimmed, hintCity, hintCountry });
        if (!GOOGLE_MAPS_API_KEY) {
            setSuggestionError('Missing Google Maps API key');
            return;
        }
        const locationNameDerived = hintLocationName ? extractCityCountryFromLocationString(hintLocationName) : { city: '', country: '' };
        const fallbackCity = locationNameDerived.city || '';
        const fallbackCountry = locationNameDerived.country || '';
        const effectiveCity = hintCity || fallbackCity;
        const effectiveCountry = hintCountry || fallbackCountry;
        const citySource = hintCity ? 'day hint' : fallbackCity ? 'location name' : 'none';
        const countrySource = hintCountry ? 'day hint' : fallbackCountry ? 'location name' : 'none';
        
        // Log what we're working with
        console.error('[HOTEL SEARCH] STEP 1 - Location hint analysis', {
            'INPUT_hintCity': hintCity,
            'INPUT_hintCountry': hintCountry,
            'INPUT_hintLocationName': hintLocationName,
            'EXTRACTED_from_locationName': locationNameDerived,
            'FALLBACK_city': fallbackCity,
            'FALLBACK_country': fallbackCountry,
            'EFFECTIVE_city': effectiveCity,
            'EFFECTIVE_country': effectiveCountry,
            'SOURCE_city': citySource,
            'SOURCE_country': countrySource,
            'dayLocationHint': dayLocationHint
        });
        
        // Build query: just the hotel name + city to keep Google focused on the right place
        const queryParts = [trimmed];
        if (effectiveCity) {
            queryParts.push(effectiveCity);
        }
        if (effectiveCountry) {
            queryParts.push(effectiveCountry);
        }
        const searchQuery = queryParts.filter(Boolean).join(' ');
        // 200 miles in meters
        const RADIUS_200_MILES = 321869;
        const params = new URLSearchParams({
            query: searchQuery,
            radius: String(RADIUS_200_MILES)
        });
        if (effectiveCity) {
            params.append('city', effectiveCity);
        }
        if (effectiveCountry) {
            params.append('country', effectiveCountry);
        }
        if (hintLocationName) {
            params.append('location_name', hintLocationName);
        }
        if (typeof hintLocationLat === 'number' && typeof hintLocationLng === 'number') {
            params.append('location_lat', hintLocationLat.toString());
            params.append('location_lng', hintLocationLng.toString());
        }
        const language = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en';
        params.append('language', language);
        const requestTimestamp = new Date().toISOString();
        const normalizedLat = hintLocationLat !== undefined ? hintLocationLat : null;
        const normalizedLng = hintLocationLng !== undefined ? hintLocationLng : null;
        const requestId = `hotel-search-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const googleDebug = {
            requestId,
            timestamp: requestTimestamp,
            dayLabel,
            dayDate,
            query: searchQuery,
            city: effectiveCity,
            country: effectiveCountry,
            locationName: hintLocationName,
            locationLat: normalizedLat,
            locationLng: normalizedLng,
            radius: RADIUS_200_MILES,
            language,
            params: params.toString()
        };
        console.info('HotelReservationDialog: location selection for google search', {
            requestId,
            hintCity,
            hintCountry,
            fallbackCity,
            fallbackCountry,
            effectiveCity,
            effectiveCountry,
            citySource,
            countrySource,
            locationName: hintLocationName,
            language
        });
        console.info('HotelReservationDialog: google search params', googleDebug);
        setLoadingSuggestions(true);
        setSuggestionError('');
        const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
        const fetchStart = now();
        try {
            const requestUrl = `${API_PATH}/google_place_textsearch.php?${params.toString()}`;
            console.error('[HOTEL SEARCH] STEP 5 - FINAL REQUEST URL:', requestUrl);
            console.error('[HOTEL SEARCH] Query parts:', {
                'searchQuery': searchQuery,
                'effectiveCity': effectiveCity,
                'effectiveCountry': effectiveCountry
            });
            console.info('HotelReservationDialog: outgoing google request', { requestId, url: requestUrl });
            const response = await fetch(requestUrl);
            const fetchEnd = now();
            const durationMs = Math.round(fetchEnd - fetchStart);
            const data = await response.json();
            const responseDebug = {
                requestId,
                statusCode: response.status,
                ok: response.ok,
                durationMs,
                results: Array.isArray(data?.results) ? data.results.length : 0,
                googleStatus: data?.status,
                errorMessage: data?.error_message || null
            };
            console.info('HotelReservationDialog: google response', responseDebug);
            if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
                throw new Error(data.error_message || data.status || 'Google Places returned an error');
            }
            setSuggestions(data.results || []);
        } catch (err) {
            console.error('HotelReservationDialog: google request failed', {
                requestId,
                hintCity,
                hintCountry,
                locationLat: normalizedLat,
                locationLng: normalizedLng,
                error: err?.message || err
            });
            setSuggestionError(err.message || 'Failed to fetch hotel suggestions');
        } finally {
            setLoadingSuggestions(false);
        }
    }, [hintCity, hintCountry, hintLocationName, hintLocationLat, hintLocationLng, dayLabel, dayDate]);

    useEffect(() => {
        const searchTerm = hotelAddress.trim();
        if (searchTerm === '') {
            setSuggestions([]);
            setSuggestionError('');
            return;
        }
        if (selectedSuggestion && searchTerm === (selectedSuggestion.formatted_address || '').trim()) {
            return;
        }
        const timer = setTimeout(() => {
            fetchHotelSuggestions(hotelAddress);
        }, 650);
        return () => clearTimeout(timer);
    }, [hotelAddress, fetchHotelSuggestions, selectedSuggestion]);

    const handleSuggestionSelect = (suggestion) => {
        console.debug('HotelReservationDialog: suggestion selected', {
            name: suggestion.name,
            address: suggestion.formatted_address,
            id: suggestion.place_id
        });
        const parsed = parseCityCountryFromAddressComponents(suggestion.address_components || []);
        setSelectedSuggestion(suggestion);
        setSelectedSuggestionId(suggestion.place_id);
        setHotelName(suggestion.name || hotelName);
        setHotelAddress(suggestion.formatted_address || hotelAddress);
        setHotelCity(parsed.city || hintCity);
        setHotelCountry(parsed.country || hintCountry);
        setSuggestions([]);
        setSuggestionError('');
    };

    const stayId = initialValues?.id || null;

    const handleDelete = () => {
        if (!stayId || !onDelete) return;
        onDelete(stayId);
    };

    const handleSave = () => {
        console.debug('HotelReservationDialog: save clicked', {
            checkInDate,
            checkOutDate,
            checkInTime,
            checkOutTime,
            hotelName,
            hotelAddress,
            hotelCity,
            hotelCountry,
            reference,
            notes,
            selectedSuggestionId
        });
        if (!isValidRange) return;
        const checkInDateTime = `${checkInDate}T${checkInTime || '15:00'}`;
        const checkOutDateTime = `${checkOutDate}T${checkOutTime || '11:00'}`;
        const trimmedReference = reference.trim();
        const trimmedNotes = notes.trim();
        const trimmedName = hotelName.trim();
        const trimmedAddress = hotelAddress.trim();
        const trimmedCity = hotelCity.trim();
        const trimmedCountry = hotelCountry.trim();
        const locationData = selectedSuggestion?.geometry?.location;
        const payload = {
            checkInDateTime,
            checkOutDateTime,
            ...(trimmedName ? { hotelName: trimmedName } : {}),
            ...(trimmedAddress ? { hotelAddress: trimmedAddress } : {}),
            ...(trimmedCity ? { hotelCity: trimmedCity } : {}),
            ...(trimmedCountry ? { hotelCountry: trimmedCountry } : {}),
            ...(selectedSuggestion ? { hotelMapPlaceId: selectedSuggestion.place_id } : {}),
            ...(locationData ? { hotelLatitude: locationData.lat, hotelLongitude: locationData.lng } : {}),
            ...(trimmedReference ? { reference: trimmedReference } : {}),
            ...(trimmedNotes ? { notes: trimmedNotes } : {})
        };
        onSave(payload);
    };

    const titleLabel = dayLabel ? `${dayLabel}${t('hotel_stay_suffix')}` : t('hotel_stay_default_title');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {titleLabel}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {stayId && !isEditMode && (
                            <Tooltip title={t('edit_stay_tooltip') || 'Edit'} arrow>
                                <IconButton aria-label="edit stay" onClick={() => setIsEditMode(true)} sx={{ color: 'primary.main' }}>
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {stayId && (
                            <Tooltip title={t('delete_stay_tooltip')} arrow>
                                <IconButton aria-label="delete stay" onClick={handleDelete} sx={{ color: 'error.main' }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {/* VIEW MODE - Show lodging info as read-only */}
                {!isEditMode && stayId ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {/* Hotel Name/Address */}
                        {hotelAddress && (
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    {t('hotel_name_label') || 'Hotel'}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {hotelAddress}
                                </Typography>
                            </Box>
                        )}
                        
                        {/* Stay Dates */}
                        <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.100' }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t('stay_dates_label') || 'Stay Dates'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">{t('check_in_label') || 'Check-in'}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {checkInDate} {checkInTime && `@ ${checkInTime}`}
                                    </Typography>
                                </Box>
                                <Typography variant="body1" color="text.secondary">→</Typography>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">{t('check_out_label') || 'Check-out'}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {checkOutDate} {checkOutTime && `@ ${checkOutTime}`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        
                        {/* Booking Reference */}
                        {reference && (
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    {t('booking_reference_label') || 'Booking Reference'}
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                    {reference}
                                </Typography>
                            </Box>
                        )}
                        
                        {/* Notes */}
                        {notes && (
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    {t('notes_label') || 'Notes'}
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {notes}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    /* EDIT MODE - Show form */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <ExpediaDateRangeInput
                            label={t('stay_dates_label')}
                            startDate={checkInDate}
                            endDate={checkOutDate}
                            onChange={handleRangeSelect}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                label={t('check_in_time_label')}
                                type="time"
                                value={checkInTime}
                                onChange={(event) => handleCheckInTimeChange(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                label={t('check_out_time_label')}
                                type="time"
                                value={checkOutTime}
                                onChange={(event) => handleCheckOutTimeChange(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                            />
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                label={t('hotel_name_label')}
                                value={hotelAddress}
                                onChange={(event) => handleAddressChange(event.target.value)}
                                fullWidth
                                multiline
                                minRows={2}
                                helperText={t('hotel_address_helper_text')}
                            />
                            {loadingSuggestions && (
                                <CircularProgress
                                    size={20}
                                    sx={{ position: 'absolute', top: 12, right: 12 }}
                                />
                            )}
                        </Box>
                        {suggestionError && (
                            <Typography variant="caption" color="error">
                                {suggestionError}
                            </Typography>
                        )}
                        {suggestions.length > 0 && (
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                <List dense>
                                    {suggestions.map((suggestion) => (
                                        <ListItemButton
                                            key={suggestion.place_id}
                                            selected={selectedSuggestionId === suggestion.place_id}
                                            onClick={() => handleSuggestionSelect(suggestion)}
                                        >
                                            <ListItemText
                                                primary={suggestion.name}
                                                secondary={suggestion.formatted_address}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                        )}
                        {!loadingSuggestions && hotelAddress.trim().length >= 4 && suggestions.length === 0 && !suggestionError && (
                            <Typography variant="caption" color="text.secondary">
                                {t('no_hotels_found')}
                            </Typography>
                        )}
                        <TextField
                            label={t('booking_reference_label')}
                            value={reference}
                            onChange={(event) => handleReferenceChange(event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label={t('notes_label')}
                            value={notes}
                            onChange={(event) => handleNotesChange(event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('cancel_btn') || 'Close'}</Button>
                {isEditMode && (
                    <Button variant="contained" onClick={handleSave} disabled={!isValidRange}>
                        {t('save_btn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ItineraryPage;